import {
  FC, Fragment, memo, useContext, useState, useEffect, useMemo,
  useCallback, useRef, ChangeEvent, DragEvent,
} from 'react';
import * as XLSX from 'xlsx';
import {
  ClipboardList, RefreshCw, Upload, Download, ChevronDown, ChevronRight,
  Search, AlertTriangle, Package,
  X, Plus, Minus, RotateCcw, ArrowUpRight, ArrowDownRight,
  CheckCircle, FileText, Layers, History, Zap, BarChart2,
} from 'lucide-react';
import { CreateGroupModal } from '../components/groups/CreateGroupModal';
import { MergeGroupsModal, MergeableGroup } from '../components/groups/MergeGroupsModal';
import { PackFloorSection } from '../components/inventory/PackFloorSection';
import { OwnFloorField } from '../components/groups/OwnFloorField';
import ContextStore from '../hooks/ContextStore';
import { useIsPhone } from '../hooks/useIsPhone';
import { dispatch } from '../dispatch/dispatch';
import axios from 'axios';
import { urls } from '../dispatch/urls';
import { actions } from '../dispatch';
import { IStoreResponse } from '../interfaces/responses/StoreResponse.interface';
import './InventoryControl.page.css';

// ─── Types ────────────────────────────────────────────────────────────────────
interface SkuInfo {
  _id: string; Sku: string; name: string;
  imgUri: string; url: string; storeName: string; storeId: string;
}

interface MovementRecord {
  _id: string; type: string; quantity: number;
  quantityBefore: number; quantityAfter: number;
  source: string; reason: string; notes: string; timestamp: string;
  unitPrice?: number | null; supplier?: string | null;
}

interface InventoryGroup {
  _id: string; name: string; imgUri: string | null;
  buyingRate: number; newMarketPrice: number; transportFees: number; storageFees: number; packageFees: number; costs: number;
  jumiaStock: number; manualStock: number | null; damagedStock: number;
  discrepancy: number | null; discrepancyPct: number | null;
  reconcStatus: 'matched' | 'leak' | 'surplus' | 'warning' | 'no-data';
  hasInventoryRecord: boolean;
  lastStockCount: string | null; lastUpdated: string | null;
  location: string; notes: string;
  totalSkus: number; skus: SkuInfo[];
  delivered30: number; returned30: number; canceled30: number;
  pending30: number; shipped30: number; rts30: number; failed30: number;
  revenue30: number; netSold30: number; impliedStarting: number | null;
  velocity7d: number; coverageDays: number;
  recentMovements: MovementRecord[];
  // market intelligence
  marketAvailability: string | null; marketTrend: string | null;
  competitionLevel: string | null; supplierCount: string | null;
  supplierQuality: string | null; marketVolume: string | null;
  productQuality: string | null; returnRateObs: string | null;
  reorderSignal: string | null; competitorPriceMin: number | null;
  competitorPriceMax: number | null; marketNotes: string | null;
  marketUpdatedAt: string | null;
  // jumia sync
  jumiaSyncEnabled: boolean; jumiaPaused: boolean; marketAvailable: boolean; safetyBuffer: number;
  lastJumiaSyncAt: string | null; lastJumiaSyncStatus: 'success' | 'partial' | 'failed' | null;
  lastJumiaSyncTarget: number | null; lastJumiaSyncDetail: string;
  // marjan sync (same rules as Jumia; per linked Marjan SKU / GTIN)
  marjanSyncEnabled: boolean; marjanPaused: boolean;
  lastMarjanSyncAt: string | null; lastMarjanSyncStatus: 'success' | 'partial' | 'failed' | null;
  lastMarjanSyncTarget: number | null; lastMarjanSyncDetail: string;
  marjanSkus: { sku: string; title: string | null; gtin: string | null; storeName: string | null; stock: number | null; missing?: boolean }[];
  totalMarjanSkus: number; marjanOpenOrders: number;
}

interface ParsedRow {
  rowNumber: number;
  rawData: Record<string, string>;
  groupName: string;
  sku: string;
  quantity: string;
  notes: string;
}

interface ImportResultRow {
  rowNumber: number;
  productGroupName: string | null;
  quantity: number | null;
  status: 'success' | 'warning' | 'error' | 'unmatched';
  issues: string[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtMAD  = (n: number) => `${Math.round(n).toLocaleString('en-US')} MAD`;
const fmt1    = (n: number) => n.toFixed(1);
const fmtDate = (s: string | null) => {
  if (!s) return '—';
  const d = new Date(s);
  return `${d.toLocaleDateString('en-GB')} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
};
const fmtDateShort = (s: string) => {
  const d = new Date(s);
  return `${d.toLocaleDateString('en-GB')} ${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}`;
};
const clamp = (n: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, n));

const MOVEMENT_CFG: Record<string, { icon: FC<any>; dotCls: string; color: string; label: string }> = {
  IN:             { icon: Plus,          dotCls: 'ic__timeline-dot--in',     color: '#1D9E75', label: 'Entrée Stock'   },
  OUT:            { icon: Minus,         dotCls: 'ic__timeline-dot--out',    color: '#E24B4A', label: 'Sortie Stock'   },
  ADJUSTMENT:     { icon: RotateCcw,     dotCls: 'ic__timeline-dot--adj',    color: '#378ADD', label: 'Adjustment'     },
  CORRECTION:     { icon: RotateCcw,     dotCls: 'ic__timeline-dot--adj',    color: '#378ADD', label: 'Correction'     },
  IMPORT:         { icon: Upload,        dotCls: 'ic__timeline-dot--import',  color: '#7C3AED', label: 'Excel Import'  },
  RETURN_GOOD:    { icon: ArrowUpRight,  dotCls: 'ic__timeline-dot--in',     color: '#1D9E75', label: 'Retour (Bon)'  },
  RETURN_DAMAGED: { icon: ArrowDownRight,dotCls: 'ic__timeline-dot--damage', color: '#EF9F27', label: 'Retour (Abîmé)'},
  DAMAGE:         { icon: AlertTriangle, dotCls: 'ic__timeline-dot--damage', color: '#EF9F27', label: 'Dommage'        },
  LOSS:           { icon: AlertTriangle, dotCls: 'ic__timeline-dot--loss',   color: '#E24B4A', label: 'Perte'          },
  SORTIE_JUMIA:   { icon: ArrowDownRight,dotCls: 'ic__timeline-dot--out',    color: '#EA580C', label: 'Sortie Jumia'  },
  SORTIE_MARJAN:  { icon: ArrowDownRight,dotCls: 'ic__timeline-dot--out',    color: '#7C3AED', label: 'Sortie Marjan' },
  RETOUR_MZIAN:   { icon: ArrowUpRight,  dotCls: 'ic__timeline-dot--in',     color: '#1D9E75', label: 'Retour Mzian'  },
  RETOUR_KHASR:   { icon: AlertTriangle, dotCls: 'ic__timeline-dot--damage', color: '#EF9F27', label: 'Retour Khasr'  },
  SORTIE_AUTRE:   { icon: Minus,         dotCls: 'ic__timeline-dot--out',    color: '#6B7280', label: 'Sortie Autre'  },
};

// ─── Movement type selector ───────────────────────────────────────────────────
const MOVE_TYPES_IN  = ['IN', 'RETURN_GOOD', 'RETOUR_MZIAN'];
const MOVE_TYPES_OUT = ['OUT', 'DAMAGE', 'RETURN_DAMAGED', 'LOSS', 'SORTIE_JUMIA', 'SORTIE_MARJAN', 'RETOUR_KHASR', 'SORTIE_AUTRE'];
const MOVE_TYPES_ADJ = ['ADJUSTMENT', 'CORRECTION'];

const ADJ_REASONS = [
  'Stock count correction', 'Supplier delivery', 'Transfer from warehouse',
  'Customer return – resellable', 'Customer return – damaged',
  'Damaged in storage', 'Lost in transit', 'Theft / unexplained loss',
  'Jumia warehouse adjustment', 'Other',
];

// ─── Import modal step labels ─────────────────────────────────────────────────
const IMPORT_STEPS = ['Upload File', 'Map Columns', 'Preview & Validate', 'Import Results'];
const SYSTEM_FIELDS = [
  { key: 'groupName', label: 'Product Group Name', required: true },
  { key: 'quantity',  label: 'Stock Quantity',      required: true },
  { key: 'sku',       label: 'SKU (optional)',       required: false },
  { key: 'notes',     label: 'Notes (optional)',     required: false },
  { key: '__ignore',  label: 'Ignore this column',  required: false },
];

// ─── Coverage bar helper ──────────────────────────────────────────────────────
const covColor = (days: number, hasSales: boolean) => {
  if (!hasSales || days === 0) return { bar: '#E5E7EB', text: 'ic__stock-num--gray' };
  if (days < 3)  return { bar: '#E24B4A', text: 'ic__stock-num--red' };
  if (days < 7)  return { bar: '#EF9F27', text: 'ic__stock-num--amber' };
  if (days < 30) return { bar: '#1D9E75', text: 'ic__stock-num--green' };
  return { bar: '#378ADD', text: 'ic__stock-num--blue' };
};

// ─── Stock movement button configs (module-level to avoid remount issues) ─────
const MOVE_BTNS_PRIMARY = [
  { t: 'SORTIE_JUMIA',  label: 'Sortie Jumia',  Icon: ArrowUpRight,   color: '#EA580C', bg: '#FFF7ED', border: '#FED7AA' },
  { t: 'SORTIE_MARJAN', label: 'Sortie Marjan', Icon: ArrowUpRight,   color: '#7C3AED', bg: '#F5F3FF', border: '#DDD6FE' },
  { t: 'RETOUR_MZIAN',  label: 'Retour Mzian',  Icon: ArrowDownRight, color: '#1D9E75', bg: '#F0FDF4', border: '#A7F3D0' },
  { t: 'RETOUR_KHASR',  label: 'Retour Khasr',  Icon: AlertTriangle,  color: '#EF9F27', bg: '#FFFBEB', border: '#FDE68A' },
  { t: 'SORTIE_AUTRE',  label: 'Sortie Autre',  Icon: ArrowUpRight,   color: '#6B7280', bg: '#F9FAFB', border: '#D1D5DB' },
];

const MOVE_BTNS_MORE = [
  { t: 'IN',         label: 'Entrée Stock', Icon: Plus,          color: '#1D9E75', bg: '#F0FDF4', border: '#A7F3D0' },
  { t: 'OUT',        label: 'Sortie Stock', Icon: Minus,         color: '#E24B4A', bg: '#FEF2F2', border: '#FECACA' },
  { t: 'DAMAGE',     label: 'Dommage',      Icon: AlertTriangle, color: '#EF9F27', bg: '#FFFBEB', border: '#FDE68A' },
  { t: 'LOSS',       label: 'Perte',        Icon: AlertTriangle, color: '#E24B4A', bg: '#FEF2F2', border: '#FECACA' },
  { t: 'ADJUSTMENT', label: 'Count Reset',  Icon: RotateCcw,     color: '#378ADD', bg: '#EFF6FF', border: '#BFDBFE' },
];

const MOVE_CONFIRM_COLOR: Record<string, string> = {
  SORTIE_JUMIA: '#EA580C', SORTIE_MARJAN: '#7C3AED',
  RETOUR_MZIAN: '#1D9E75', RETOUR_KHASR: '#EF9F27', SORTIE_AUTRE: '#6B7280',
  IN: '#1D9E75', OUT: '#E24B4A', DAMAGE: '#EF9F27', LOSS: '#E24B4A', ADJUSTMENT: '#378ADD',
};

// ─── Movement timeline item ───────────────────────────────────────────────────

// ── Full stock history of one product (user 2026-10-25) ─────────────────────────
// The list only carried the 300 newest movements of ALL products, so most products showed "no movements".
// GET /inventory/movements/:groupId → every movement + a purchases summary; filter by kind.
const HISTORY_FILTERS: { key: string; label: string; test: (m: MovementRecord) => boolean }[] = [
  { key: 'all',     label: 'Tout',        test: () => true },
  { key: 'achat',   label: '🛒 Achats',    test: (m) => m.type === 'IN' && /achat/i.test(m.reason || '') },
  { key: 'out',     label: '📦 Sorties',   test: (m) => ['OUT', 'SORTIE_JUMIA', 'SORTIE_MARJAN', 'SORTIE_AUTRE'].includes(m.type) },
  { key: 'returns', label: '↩️ Retours',   test: (m) => ['RETURN_GOOD', 'RETURN_DAMAGED', 'RETOUR_MZIAN', 'RETOUR_KHASR'].includes(m.type) },
  { key: 'fix',     label: '✏️ Corrections', test: (m) => ['ADJUSTMENT', 'CORRECTION', 'IMPORT', 'DAMAGE', 'LOSS'].includes(m.type) || (m.type === 'IN' && !/achat/i.test(m.reason || '')) },
];
const StockHistory: FC<{ groupId: string }> = ({ groupId }) => {
  const { contextStore } = useContext(ContextStore);
  const [data, setData] = useState<{ movements: MovementRecord[]; purchases: { count: number; pieces: number; lowestPrice: number | null; lastPrice: number | null; lastAt: string | null; spent: number } } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState('all');
  const [shown, setShown] = useState(30);
  useEffect(() => {
    let alive = true;
    axios.get(`${urls.baseUrlRestApi}/inventory/movements/${groupId}`, { headers: { 'x-auth-token': contextStore.token || '' } })
      .then((r) => { if (alive) setData(r.data); }).catch((e) => alive && setErr(e?.response?.data?.error?.message || e.message));
    return () => { alive = false; };
  }, [groupId, contextStore.token]);
  const f = HISTORY_FILTERS.find((x) => x.key === filter) || HISTORY_FILTERS[0];
  const list = (data?.movements || []).filter(f.test);
  const p = data?.purchases;
  return (
    <div className="ic__detail-card">
      <div className="ic__detail-card-title">
        <History size={11} style={{ display: 'inline', marginRight: 4 }} />
        Movement History {data ? `(${data.movements.length})` : ''}
      </div>
      {err && <div style={{ fontSize: '0.78rem', color: '#DC2626' }}>{err}</div>}
      {!data && !err && <div style={{ fontSize: '0.78rem', color: '#9CA3AF', padding: '0.5rem 0' }}>Loading…</div>}
      {data && p && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 6, margin: '4px 0 8px' }}>
          {[
            ['🛒 Achats', p.count ? `${p.count} · ${p.pieces} pcs` : '—'],
            ['Dernier achat', p.lastAt ? new Date(p.lastAt).toLocaleDateString('fr-FR') : '—'],
            ['Dernier prix', p.lastPrice != null ? `${p.lastPrice} MAD` : '—'],
            ['Prix le plus bas', p.lowestPrice != null ? `${p.lowestPrice} MAD` : '—'],
          ].map(([k, v]) => (
            <div key={k} style={{ background: '#F0FDF4', borderRadius: 8, padding: '6px 8px' }}>
              <div style={{ fontSize: '0.68rem', color: '#6B7280' }}>{k}</div>
              <div style={{ fontSize: '0.86rem', fontWeight: 700, color: '#065F46' }}>{v}</div>
            </div>
          ))}
        </div>
      )}
      {data && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 6 }}>
          {HISTORY_FILTERS.map((x) => {
            const n = data.movements.filter(x.test).length;
            return <button key={x.key} onClick={() => { setFilter(x.key); setShown(30); }} style={{ fontSize: '0.74rem', fontWeight: 600, padding: '3px 8px', borderRadius: 999, border: '1px solid #E5E7EB', background: filter === x.key ? '#111827' : '#fff', color: filter === x.key ? '#fff' : '#374151', cursor: 'pointer' }}>{x.label} {n}</button>;
          })}
        </div>
      )}
      {data && list.length === 0 && <div style={{ fontSize: '0.78rem', color: '#9CA3AF', padding: '0.5rem 0' }}>{filter === 'achat' ? 'Aucun achat enregistré pour ce produit (le stock est venu d’un import ou d’un comptage).' : 'Aucun mouvement.'}</div>}
      {list.length > 0 && <div className="ic__timeline">{list.slice(0, shown).map((m) => <MovementItem key={m._id} m={m} />)}</div>}
      {list.length > shown && <button onClick={() => setShown(shown + 50)} style={{ marginTop: 6, fontSize: '0.78rem', fontWeight: 600, color: '#2563EB', background: 'none', border: 'none', cursor: 'pointer' }}>Voir plus ({list.length - shown})</button>}
    </div>
  );
};

const MovementItem: FC<{ m: MovementRecord }> = memo(({ m }) => {
  const cfg = MOVEMENT_CFG[m.type] ?? MOVEMENT_CFG['ADJUSTMENT'];
  const Icon = cfg.icon;
  const isIn = ['IN', 'RETURN_GOOD', 'IMPORT'].includes(m.type);
  return (
    <div className="ic__timeline-item">
      <div className={`ic__timeline-dot ${cfg.dotCls}`}>
        <Icon size={12} color={cfg.color} strokeWidth={2.5} />
      </div>
      <div className="ic__timeline-body">
        <div className="ic__timeline-top">
          <span className="ic__timeline-type" style={{ color: cfg.color }}>{cfg.label}</span>
          <span className="ic__timeline-qty">
            {isIn ? '+' : '-'}{m.quantity}u
            <span style={{ color: '#9CA3AF', fontWeight: 400 }}>
              {' '}({m.quantityBefore} → {m.quantityAfter})
            </span>
          </span>
          <span className="ic__timeline-src">{m.source}</span>
        </div>
        {m.reason && <div className="ic__timeline-reason">"{m.reason}"</div>}
        {(m.unitPrice || m.supplier) ? <div className="ic__timeline-reason" style={{ color: '#047857', fontStyle: 'normal', fontWeight: 600 }}>💵 {m.unitPrice ? `${m.unitPrice} MAD / pièce` : 'prix non saisi'}{m.supplier ? ` · ${m.supplier}` : ''}</div> : null}
      </div>
      <span className="ic__timeline-time">{fmtDateShort(m.timestamp)}</span>
    </div>
  );
});

type AdjState = { moveType: string; qty: string; reason: string; notes: string; saving: boolean };
type SyncUiState = { busy: boolean; bufferDraft?: string; msg: string | null; msgKind: 'ok' | 'err' | null };

interface DetailPanelProps {
  g: InventoryGroup;
  adj: AdjState;
  onSetAdj: (patch: Partial<AdjState>) => void;
  onApplyMovement: () => void;
  onApplyDirectCount: (qty: string) => void;
  sync: SyncUiState;
  onToggleSync: (enabled: boolean) => void;
  onTogglePause: (paused: boolean) => void;
  onToggleMarket: (on: boolean) => void;
  onSetBufferDraft: (v: string) => void;
  onSaveBuffer: (v: string) => void;
  onSyncNow: () => void;
  onToggleMarjanSync: (enabled: boolean) => void;
  onToggleMarjanPause: (paused: boolean) => void;
  onSyncMarjanNow: () => void;
}

// ─── Expanded detail panel ────────────────────────────────────────────────────
// Defined at module level so React sees a stable component type across renders.
// Typing in the qty input no longer loses focus.
const DetailPanel: FC<DetailPanelProps> = memo(({ g, adj, onSetAdj, onApplyMovement, onApplyDirectCount, sync, onToggleSync, onTogglePause, onToggleMarket, onSetBufferDraft, onSaveBuffer, onSyncNow, onToggleMarjanSync, onToggleMarjanPause, onSyncMarjanNow }) => {
  const isIn  = MOVE_TYPES_IN.includes(adj.moveType);
  const isOut = MOVE_TYPES_OUT.includes(adj.moveType);
  const isAdj = MOVE_TYPES_ADJ.includes(adj.moveType);

  const maxStock = Math.max(g.manualStock ?? 0, 1);
  const cc = covColor(g.coverageDays, g.velocity7d > 0);

  const typeActiveCls = (t: string) =>
    adj.moveType === t
      ? `ic__adj-type-btn ${isIn ? 'ic__adj-type-btn--active-in' : isOut ? 'ic__adj-type-btn--active-out' : 'ic__adj-type-btn--active-adj'}`
      : 'ic__adj-type-btn';

  return (
    <div className="ic__detail-panel">
      <div className="ic__detail-grid">

        {/* ── Reconciliation card ── */}
        <div className="ic__detail-card">
          <div className="ic__detail-card-title">Stock Overview</div>
          <div className="ic__recon-bar-wrap">
            <div className="ic__recon-bar-row">
              <span className="ic__recon-bar-label">Manual</span>
              <div className="ic__recon-bar-track">
                <div className="ic__recon-bar-fill"
                  style={{ width: `${clamp(((g.manualStock ?? 0) / maxStock) * 100, 0, 100)}%`, background: '#378ADD' }} />
              </div>
              <span className="ic__recon-bar-val ic__stock-num--blue">
                {g.manualStock !== null ? g.manualStock : '—'}
              </span>
            </div>
            {g.damagedStock > 0 && (
              <div className="ic__recon-bar-row">
                <span className="ic__recon-bar-label">Damaged</span>
                <div className="ic__recon-bar-track">
                  <div className="ic__recon-bar-fill"
                    style={{ width: `${clamp((g.damagedStock / maxStock) * 100, 0, 100)}%`, background: '#EF9F27' }} />
                </div>
                <span className="ic__recon-bar-val ic__stock-num--amber">{g.damagedStock}</span>
              </div>
            )}
          </div>
          <div className="ic__recon-rows" style={{ marginTop: '0.875rem' }}>
            <div className="ic__recon-row">
              <span className="ic__recon-label">Sold 30d (net)</span>
              <span className="ic__recon-value">{g.netSold30} units</span>
            </div>
            <div className="ic__recon-row">
              <span className="ic__recon-label">Coverage</span>
              <span className="ic__recon-value" style={{ color: cc.bar }}>
                {g.velocity7d === 0 ? 'No sales' : g.coverageDays >= 9999 ? '∞' : `${fmt1(g.coverageDays)}d`}
              </span>
            </div>
            <div className="ic__recon-row">
              <span className="ic__recon-label">Last count</span>
              <span className="ic__recon-value" style={{ fontSize: '0.72rem' }}>{fmtDate(g.lastStockCount)}</span>
            </div>
            <div className="ic__recon-row">
              <span className="ic__recon-label">New Market Price</span>
              <span className="ic__recon-value" style={{ fontWeight: 700, color: '#378ADD' }}>
                {g.newMarketPrice > 0 ? `${g.newMarketPrice.toFixed(2)} MAD` : '—'}
              </span>
            </div>
            {g.location && (
              <div className="ic__recon-row">
                <span className="ic__recon-label">Location</span>
                <span className="ic__recon-value">{g.location}</span>
              </div>
            )}
          </div>
        </div>

        {/* ── Order stats card ── */}
        <div className="ic__detail-card">
          <div className="ic__detail-card-title">Order Impact (30 days)</div>
          <div className="ic__order-grid">
            {[
              { val: g.delivered30, lbl: 'Delivered', color: '#1D9E75' },
              { val: g.returned30,  lbl: 'Returned',  color: '#E24B4A' },
              { val: g.canceled30,  lbl: 'Canceled',  color: '#6B7280' },
              { val: g.failed30,    lbl: 'Failed',    color: '#9CA3AF' },
              { val: g.pending30,   lbl: 'Pending',   color: '#378ADD' },
              { val: g.rts30,       lbl: 'Ready→Ship',color: '#7C3AED' },
            ].map(s => (
              <div key={s.lbl} className="ic__order-stat">
                <span className="ic__order-stat-val" style={{ color: s.color }}>{s.val}</span>
                <span className="ic__order-stat-lbl">{s.lbl}</span>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '0.875rem', borderTop: '0.5px solid #F3F4F6', paddingTop: '0.625rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
              <span style={{ color: '#6B7280' }}>Revenue 30d</span>
              <span style={{ fontWeight: 600, color: '#1D9E75' }}>{fmtMAD(g.revenue30)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginTop: 4 }}>
              <span style={{ color: '#6B7280' }}>Velocity</span>
              <span style={{ fontWeight: 600 }}>{fmt1(g.velocity7d)} orders/day</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', marginTop: 4 }}>
              <span style={{ color: '#6B7280' }}>Unit cost</span>
              <span style={{ fontWeight: 600 }}>{fmtMAD(g.costs)}</span>
            </div>
          </div>
        </div>

        {/* ── Stock adjustment card ── */}
        <div className="ic__detail-card">
          <div className="ic__detail-card-title">Stock Movement</div>
          <div className="ic__adj-row">
            <div>
              <div className="ic__adj-label">Movement type</div>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
                {[
                  { t: 'SORTIE_JUMIA',  label: '↓ Sortie Jumia'  },
                  { t: 'SORTIE_MARJAN', label: '↓ Sortie Marjan' },
                  { t: 'RETOUR_MZIAN',  label: '↑ Retour Mzian'  },
                  { t: 'RETOUR_KHASR',  label: '⚠ Retour Khasr'  },
                  { t: 'SORTIE_AUTRE',  label: '↓ Sortie Autre'  },
                  { t: 'ADJUSTMENT',    label: '⟳ Count'          },
                ].map(({ t, label }) => (
                  <button key={t}
                    className={typeActiveCls(t)}
                    onClick={() => onSetAdj({ moveType: t })}
                  >{label}</button>
                ))}
              </div>
            </div>

            <div>
              <div className="ic__adj-label">
                {isAdj ? 'New total stock count' : 'Quantity'}
              </div>
              <input
                type="number" min={0} placeholder={isAdj ? `Current: ${g.manualStock ?? '?'}` : '0'}
                className="ic__adj-input"
                value={adj.qty}
                onChange={e => onSetAdj({ qty: e.target.value })}
              />
            </div>

            <div>
              <div className="ic__adj-label">Reason</div>
              <select className="ic__adj-select"
                value={adj.reason}
                onChange={e => onSetAdj({ reason: e.target.value })}
              >
                <option value="">Select reason…</option>
                {ADJ_REASONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            <div>
              <div className="ic__adj-label">Notes (optional)</div>
              <input type="text" placeholder="Additional details…"
                className="ic__adj-input"
                value={adj.notes}
                onChange={e => onSetAdj({ notes: e.target.value })}
              />
            </div>

            <button
              className={`ic__adj-btn ${isAdj ? 'ic__adj-btn--adj' : isIn ? 'ic__adj-btn--in' : 'ic__adj-btn--out'}`}
              disabled={adj.saving || !adj.qty}
              onClick={() => isAdj ? onApplyDirectCount(adj.qty) : onApplyMovement()}
            >
              {adj.saving ? 'Saving…' : isAdj ? `Set Stock to ${adj.qty || '?'}` : `Record ${adj.moveType.replace('_', ' ')}`}
            </button>
          </div>
        </div>
      </div>

      {/* ── Stock sync — ONE rule, ONE pause, ONE sync for every SKU on Jumia AND Marjane (2026-10-13) ── */}
      {(() => {
        const bufShown  = sync.bufferDraft !== undefined ? sync.bufferDraft : String(g.safetyBuffer);
        const bufNum    = parseInt(bufShown, 10);
        // one physical stock: open orders of BOTH marketplaces are deducted
        const openOrders = g.pending30 + g.rts30 + (g.marjanOpenOrders || 0);
        const available = Math.max(0, (g.manualStock ?? 0) - openOrders);
        const estTarget = Math.max(0, available - (isNaN(bufNum) ? g.safetyBuffer : bufNum));
        const paused    = g.jumiaPaused || g.marjanPaused;
        const isDrip    = !paused && estTarget === 0 && available > 0;
        const isMarket  = !paused && g.marketAvailable && available === 0;
        const mGtins    = g.marjanSkus.filter(s => !s.missing).length;
        const missing   = g.marjanSkus.filter(s => s.missing).length;
        const color = (st?: string | null) => st === 'success' ? '#1D9E75' : st === 'partial' ? '#EF9F27' : st === 'failed' ? '#E24B4A' : '#9CA3AF';
        return (
          <div className="ic__detail-card" style={{ marginBottom: '0.875rem' }}>
            <div className="ic__detail-card-title">
              <Zap size={11} style={{ display: 'inline', marginRight: 4 }} />
              Stock sync — the warehouse controls every SKU: {g.totalSkus} on Jumia{g.totalMarjanSkus ? ` + ${mGtins} on Marjane` : ''}
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '1.25rem' }}>
              <div style={{ fontSize: '0.8rem', color: '#374151' }}>
                {paused ? (
                  <span style={{ color: '#E24B4A', fontWeight: 700 }}>⏸ Paused — 0 on every SKU, Jumia and Marjane</span>
                ) : isDrip ? (
                  <span>Low stock: <strong style={{ color: '#EF9F27' }}>1 unit</strong> on the best-selling SKU only <span style={{ color: '#9CA3AF' }}>({available} left — sold 1 by 1)</span></span>
                ) : isMarket ? (
                  <span>🛒 Disponible au marché: <strong style={{ color: openOrders > 0 ? '#E24B4A' : '#7C3AED' }}>{openOrders > 0 ? 0 : 1}</strong> on every SKU{openOrders > 0 ? ` — ${openOrders} open order${openOrders !== 1 ? 's' : ''}, back to 1 when shipped` : ''}</span>
                ) : (
                  <span>
                    Every SKU shows <strong style={{ color: '#EA580C', fontSize: '0.9rem' }}>{estTarget}</strong>
                    <span style={{ color: '#9CA3AF' }}> (stock {g.manualStock ?? 0} − {openOrders} open order{openOrders !== 1 ? 's' : ''} − buffer {isNaN(bufNum) ? g.safetyBuffer : bufNum})</span>
                  </span>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: '0.72rem', color: '#6B7280' }}>Safety buffer</span>
                <input type="number" min={0} className="ic__adj-input" style={{ width: 64 }} value={bufShown} onChange={e => onSetBufferDraft(e.target.value)} />
                {sync.bufferDraft !== undefined && sync.bufferDraft !== String(g.safetyBuffer) && (
                  <button className="ic__topbar-btn ic__topbar-btn--primary" disabled={sync.busy || isNaN(bufNum) || bufNum < 0} onClick={() => onSaveBuffer(bufShown)}>Save</button>
                )}
              </div>

              <button className="ic__topbar-btn"
                style={{ background: paused ? '#F0FDF4' : '#FEF2F2', color: paused ? '#1D9E75' : '#E24B4A', border: `1px solid ${paused ? '#A7F3D0' : '#FECACA'}`, fontWeight: 700 }}
                disabled={sync.busy}
                onClick={() => onTogglePause(!paused)}
                title={paused ? 'Sell again everywhere: the stock goes back to every SKU on Jumia and Marjane' : 'Keep the inventory but show 0 on every SKU, Jumia and Marjane'}
              >
                {paused ? '▶ Resume' : '⏸ Pause'}
              </button>

              <button className="ic__topbar-btn"
                style={{ background: g.marketAvailable ? '#F5F3FF' : '#F9FAFB', color: g.marketAvailable ? '#7C3AED' : '#6B7280', border: `1px solid ${g.marketAvailable ? '#DDD6FE' : '#D1D5DB'}`, fontWeight: 700 }}
                disabled={sync.busy || paused}
                onClick={() => onToggleMarket(!g.marketAvailable)}
                title={g.marketAvailable ? 'Market mode ON: with no stock here, every SKU shows 1; an order drops them all to 0 until it ships. Click to turn off.' : 'No stock here but you can buy it at the market: 1 on every SKU to test demand; an order drops them all to 0 until it ships'}
              >
                {g.marketAvailable ? '🛒 Dispo. au marché ✓' : '🛒 Disponible au marché'}
              </button>

              <button className="ic__topbar-btn ic__topbar-btn--blue" disabled={sync.busy} onClick={() => { onSyncNow(); if (g.totalMarjanSkus) onSyncMarjanNow(); }}>
                <RefreshCw size={12} className={sync.busy ? 'ic__spin' : ''} />
                {sync.busy ? 'Syncing…' : 'Sync now'}
              </button>
            </div>

            {sync.msg && <div style={{ marginTop: '0.5rem', fontSize: '0.74rem', fontWeight: 600, color: sync.msgKind === 'err' ? '#E24B4A' : '#1D9E75' }}>{sync.msg}</div>}
            {(g.lastJumiaSyncAt || g.lastMarjanSyncAt) && (
              <div style={{ marginTop: '0.4rem', fontSize: '0.7rem', color: '#9CA3AF' }}>
                Last sync — {g.lastJumiaSyncAt && <>Jumia <span style={{ color: color(g.lastJumiaSyncStatus), fontWeight: 600 }}>{g.lastJumiaSyncStatus || '—'}</span> {fmtDate(g.lastJumiaSyncAt)}</>}
                {g.lastMarjanSyncAt && <>{g.lastJumiaSyncAt ? ' · ' : ''}Marjane <span style={{ color: color(g.lastMarjanSyncStatus), fontWeight: 600 }}>{g.lastMarjanSyncStatus || '—'}</span> {fmtDate(g.lastMarjanSyncAt)}</>}
                {g.lastJumiaSyncStatus && g.lastJumiaSyncStatus !== 'success' && g.lastJumiaSyncDetail && <span style={{ color: '#E24B4A' }}> — {g.lastJumiaSyncDetail}</span>}
                {g.lastMarjanSyncStatus && g.lastMarjanSyncStatus !== 'success' && g.lastMarjanSyncDetail && <span style={{ color: '#E24B4A' }}> — {g.lastMarjanSyncDetail}</span>}
              </div>
            )}
            {g.totalMarjanSkus > 0 && (
              <details style={{ marginTop: '0.4rem', fontSize: '0.7rem', color: '#6B7280' }}>
                <summary style={{ cursor: 'pointer' }}>Marjane listings ({g.totalMarjanSkus}){missing > 0 ? ` — ${missing} not in the catalog cache` : ''}</summary>
                <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {g.marjanSkus.map((s, i) => (
                    <span key={s.sku + (s.gtin || '') + i} title={s.title || s.sku} style={{ padding: '2px 8px', borderRadius: 999, border: `1px solid ${s.missing ? '#FECACA' : '#B2E5E3'}`, background: s.missing ? '#FEF2F2' : '#EDFAF9', color: s.missing ? '#B91C1C' : '#0E6F6C' }}>
                      {s.sku}{s.storeName ? ` · ${s.storeName}` : ''}{s.missing ? ' · not in catalog cache' : ` · shows ${s.stock ?? '—'}`}
                    </span>
                  ))}
                </div>
              </details>
            )}
          </div>
        );
      })()}

      {/* ── SKU breakdown ── */}
      {g.skus.length > 0 && (
        <div className="ic__sku-wrap">
          <div className="ic__sku-table-title">
            {g.skus.length} SKU{g.skus.length !== 1 ? 's' : ''} in this group
          </div>
          <table className="ic__sku-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>IMG</th>
                <th>SKU / Name</th>
                <th>Store</th>
                <th>URL</th>
              </tr>
            </thead>
            <tbody>
              {g.skus.map(sku => (
                <tr key={sku._id}>
                  <td>
                    {sku.imgUri
                      ? <img src={sku.imgUri} alt="" className="ic__sku-img" />
                      : <div className="ic__sku-img-placeholder">📦</div>
                    }
                  </td>
                  <td>
                    <span className="ic__sku-name">{sku.name || sku.Sku || '—'}</span>
                    <span className="ic__sku-id">{sku.Sku}</span>
                  </td>
                  <td style={{ color: '#6B7280', fontSize: '0.72rem' }}>{sku.storeName || '—'}</td>
                  <td>
                    {sku.url
                      ? <a href={sku.url} target="_blank" rel="noreferrer"
                          style={{ fontSize: '0.7rem', color: '#378ADD' }}>View on Jumia</a>
                      : <span style={{ fontSize: '0.7rem', color: '#9CA3AF' }}>—</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Movement history: the product's FULL history, loaded when it is opened (user 2026-10-25) ── */}
      <StockHistory groupId={g._id} />
    </div>
  );
});

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
// ⏸ why a product is paused (user 2026-10-27) — shown again when new stock is bought for it. Cancel = do not pause;
// a browser that blocks prompts pauses without a reason
const askPauseReason = (): string | null | false => {
  try { const r = window.prompt('Why pause it? (broken pieces, damaged packaging, not found in the warehouse… — optional)', ''); return r === null ? false : r.trim() || null; }
  catch { return null; }
};

// 🔔 paused products that just received new stock — resume them or keep them paused (admin)
const PauseReminders: FC<{ onChange: () => void }> = ({ onChange }) => {
  const { contextStore } = useContext(ContextStore);
  const [rows, setRows] = useState<{ id: string; groupId: string; name: string; image: string | null; pausedAt: string | null; pauseReason: string | null; bought: number; lastBuyAt: string; stock: number | null; stillPaused: boolean }[]>([]);
  const h = { headers: { 'x-auth-token': contextStore.token || '' } };
  const load = useCallback(() => { axios.get(`${urls.baseUrlRestApi}/inventory/pause-reminders`, h).then((r) => setRows(r.data || [])).catch(() => {}); window.dispatchEvent(new Event('pause-reminders-changed')); }, [contextStore.token]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);
  if (!rows.length) return null;
  const d = (x: string | null) => (x ? new Date(x).toISOString().slice(0, 10) : '—');
  const resume = async (r: typeof rows[0]) => { await axios.put(`${urls.baseUrlRestApi}/inventory/sync-settings/${r.groupId}`, { jumiaPaused: false, marjanPaused: false }, h); load(); onChange(); };
  const keep = async (r: typeof rows[0]) => { let note: string | null = null; try { note = window.prompt('Keep it paused — why? (optional)', ''); if (note === null) return; } catch { /* blocked */ } await axios.post(`${urls.baseUrlRestApi}/inventory/pause-reminders/${r.id}/keep`, { note }, h); load(); };
  return (
    <div style={{ margin: '0 0 12px', background: '#fffbeb', border: '2px solid #fcd34d', borderRadius: 14, padding: 10 }}>
      <div style={{ fontWeight: 900, fontSize: 14, marginBottom: 6 }}>🔔 Paused products that received new stock ({rows.length}) — resume them?</div>
      {rows.map((r) => (
        <div key={r.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, background: '#fff', borderRadius: 10, padding: 8, marginBottom: 6, fontSize: 13 }}>
          {r.image ? <img src={r.image} alt="" style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 8 }} /> : null}
          <div style={{ flex: 1, minWidth: 200 }}>
            <div style={{ fontWeight: 800 }}>{r.name}</div>
            <div style={{ color: '#64748b', fontSize: 12 }}>Paused since {d(r.pausedAt)}{r.pauseReason ? ` — "${r.pauseReason}"` : ' (no reason given)'} · bought +{r.bought} on {d(r.lastBuyAt)} · stock now <b>{r.stock ?? '—'}</b></div>
          </div>
          <button onClick={() => resume(r)} style={{ border: 'none', borderRadius: 8, background: '#047857', color: '#fff', fontWeight: 800, padding: '6px 12px', cursor: 'pointer' }}>▶ Resume</button>
          <button onClick={() => keep(r)} style={{ border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', color: '#475569', fontWeight: 700, padding: '6px 10px', cursor: 'pointer' }}>Keep paused</button>
        </div>
      ))}
    </div>
  );
};

const InventoryControlPage: FC = () => {
  const { contextStore } = useContext(ContextStore);
  const token = contextStore.token;

  const [stores,       setStores]       = useState<IStoreResponse[]>([]);
  const [groups,       setGroups]       = useState<InventoryGroup[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [storeId,      setStoreId]      = useState('0');
  const [expandedId,   setExpandedId]   = useState<string | null>(null);
  // phone layout (user 2026-10-15: "optimized for phones") — cards instead of the 10-column table
  const isPhone = useIsPhone();
  const [phoneLimit, setPhoneLimit] = useState(40);
  const [moreOpen, setMoreOpen] = useState(false);
  const renderSecondaryActions = () => (<>
        <button className="ic__topbar-btn ic__topbar-btn--blue"
          onClick={startBulkSync}
          disabled={bulkSyncUi?.running}
          title="Re-push warehouse stock to Jumia for every group with auto-sync enabled"
        >
          <Zap size={13} />
          {bulkSyncUi?.running
            ? `Syncing ${bulkSyncUi.done}/${bulkSyncUi.total || '…'}`
            : bulkSyncUi
              ? `Synced ${bulkSyncUi.done - bulkSyncUi.errors}/${bulkSyncUi.total}${bulkSyncUi.errors ? ` (${bulkSyncUi.errors} err)` : ''}`
              : 'Sync Jumia'}
        </button>

        <button className="ic__topbar-btn ic__topbar-btn--blue"
          style={{ background: '#00afaa', borderColor: '#00afaa' }}
          onClick={startBulkMarjan}
          disabled={bulkMarjanUi?.running}
          title="Re-push warehouse stock to Marjan for every group with a linked Marjan SKU and auto-sync enabled"
        >
          <Zap size={13} />
          {bulkMarjanUi?.running
            ? `Marjan ${bulkMarjanUi.done}/${bulkMarjanUi.total || '…'}`
            : bulkMarjanUi
              ? `Marjan ${bulkMarjanUi.done - bulkMarjanUi.errors}/${bulkMarjanUi.total}${bulkMarjanUi.errors ? ` (${bulkMarjanUi.errors} err)` : ''}`
              : 'Sync Marjan'}
        </button>

        <button className="ic__topbar-btn ic__topbar-btn--green"
          onClick={exportToExcel} disabled={filtered.length === 0}
        >
          <Download size={13} /> Export Excel
        </button>

        <button className="ic__topbar-btn ic__topbar-btn--blue"
          onClick={() => { resetImport(); setShowImport(true); }}
        >
          <Upload size={13} /> Import Excel
        </button>

  </>);
  const [search,       setSearch]       = useState('');
  const [filterStock,  setFilterStock]  = useState('all');
  // the product's own floor (MAD profit) — beats every pricing rule, Jumia + Marjane (user 2026-10-15)
  const [ownFloors, setOwnFloors] = useState<Record<string, number>>({});
  useEffect(() => {
    const load = () => axios.get(`${urls.baseUrlRestApi}/inventory/own-floors`, { headers: { 'x-auth-token': contextStore.token || '' } })
      .then(r => setOwnFloors(Object.fromEntries((r.data?.floors || []).map((f: any) => [f._id, f.floor])))).catch(() => {});
    load(); window.addEventListener('own-floor-changed', load);
    return () => window.removeEventListener('own-floor-changed', load);
  }, [contextStore.token]);
  const [sortCol, setSortCol]   = useState<string>('manual');
  const [sortDir, setSortDir]   = useState<'asc' | 'desc'>('desc');
  const [lastUpdated,  setLastUpdated]  = useState<Date | null>(null);

  // create group + merge
  const [showCreateGroup,  setShowCreateGroup]  = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState<Set<string>>(new Set());
  const [showMergeGroups,  setShowMergeGroups]  = useState(false);

  // import modal
  const [showImport,   setShowImport]   = useState(false);
  const [importStep,   setImportStep]   = useState(0);
  const [rawHeaders,   setRawHeaders]   = useState<string[]>([]);
  const [rawRows,      setRawRows]      = useState<string[][]>([]);
  const [colMap,       setColMap]       = useState<Record<string, string>>({});
  const [fileName,     setFileName]     = useState('');
  const [previewRows,  setPreviewRows]  = useState<ParsedRow[]>([]);
  const [importResult, setImportResult] = useState<any>(null);
  const [importing,    setImporting]    = useState(false);
  const [dragOver,     setDragOver]     = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [stockModalGroupId,   setStockModalGroupId]   = useState<string | null>(null);
  const [achatGroupId,        setAchatGroupId]        = useState<string | null>(null);
  const [marketGroupId,       setMarketGroupId]       = useState<string | null>(null);
  const [showMoreMoveOptions, setShowMoreMoveOptions] = useState(false);
  const [editingNameId,       setEditingNameId]       = useState<string | null>(null);
  const [editingNameValue,    setEditingNameValue]    = useState('');
  const [imgPopup, setImgPopup] = useState<{ src: string; x: number; y: number } | null>(null);

  // per-row adjustment state (keyed by groupId)
  const [adjState, setAdjState] = useState<Record<string, {
    moveType: string; qty: string; reason: string; notes: string; saving: boolean;
  }>>({});

  // ── Fetch stores ────────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      const resp = await dispatch(actions.getStores, {}, token);
      if (resp && !resp.error) setStores(resp);
    })();
  }, [token]);

  // ── Fetch inventory summary ─────────────────────────────────────────────────
  const [loadError, setLoadError] = useState<string | null>(null);
  const fetchData = useCallback(async () => {
    setLoading(true); setLoadError(null);
    try {
      const data = await dispatch(
        actions.getInventorySummary, {}, token, { storeId }
      );
      if (Array.isArray(data)) {
        setGroups(data as InventoryGroup[]);
        setLastUpdated(new Date());
      } else setLoadError((data as any)?.error?.message || 'the server did not answer (check the connection)');
    } catch (e: any) {
      setLoadError(e?.message || 'network error');
    } finally {
      setLoading(false);
    }
  }, [storeId, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── KPIs ───────────────────────────────────────────────────────────────────
  const kpis = useMemo(() => {
    const withData   = groups.filter(g => g.hasInventoryRecord);
    const noData     = groups.filter(g => !g.hasInventoryRecord);
    const damaged    = groups.reduce((s, g) => s + g.damagedStock, 0);
    const totalManual = withData.reduce((s, g) => s + (g.manualStock ?? 0), 0);
    return { withData: withData.length, noData: noData.length,
             totalManual, damaged, total: groups.length };
  }, [groups]);

  // ── Sort toggle ────────────────────────────────────────────────────────────
  const toggleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  // ── Stock frequency map (all groups, unfiltered) ──────────────────────────
  const stockFrequency = useMemo(() => {
    const map = new Map<string, number>();
    groups.forEach(g => {
      const key = g.manualStock == null ? '__nodata' : String(g.manualStock);
      map.set(key, (map.get(key) ?? 0) + 1);
    });
    return map;
  }, [groups]);

  // sorted unique stock values for the dropdown
  const stockOptions = useMemo(() => {
    const entries: { key: string; label: string; count: number }[] = [];
    stockFrequency.forEach((count, key) => {
      if (key === '__nodata') {
        entries.push({ key, label: 'No Data', count });
      } else {
        entries.push({ key, label: key, count });
      }
    });
    entries.sort((a, b) => {
      if (a.key === '__nodata') return 1;
      if (b.key === '__nodata') return -1;
      return Number(a.key) - Number(b.key);
    });
    return entries;
  }, [stockFrequency]);

  // ── Filtered + sorted list ─────────────────────────────────────────────────
  const filtered = useMemo(() => {
    let r = [...groups];
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(g => (g.name || '').toLowerCase().includes(q));
    }
    if (filterStock === '__floor') r = r.filter(g => ownFloors[g._id] != null);
    else if (filterStock !== 'all') {
      if (filterStock === '__nodata') {
        r = r.filter(g => g.manualStock == null);
      } else {
        r = r.filter(g => g.manualStock === Number(filterStock));
      }
    }

    if (sortCol) {
      const dir = sortDir === 'asc' ? 1 : -1;
      r.sort((a, b) => {
        switch (sortCol) {
          case 'manual':    return ((a.manualStock ?? -Infinity) - (b.manualStock ?? -Infinity)) * dir;
          case 'damaged':   return (a.damagedStock - b.damagedStock) * dir;
          case 'orders':    return ((a.delivered30 + a.pending30 + a.shipped30 + a.rts30) -
                                    (b.delivered30 + b.pending30 + b.shipped30 + b.rts30)) * dir;
          case 'coverage':  return (a.coverageDays - b.coverageDays) * dir;
          case 'lastcount': return ((a.lastStockCount ?? '').localeCompare(b.lastStockCount ?? '')) * dir;
          default: return 0;
        }
      });
    }
    return r;
  }, [groups, search, filterStock, sortCol, sortDir, ownFloors]);

  // ── Adjustment helpers ─────────────────────────────────────────────────────
  const getAdj = (id: string) =>
    adjState[id] ?? { moveType: 'SORTIE_JUMIA', qty: '', reason: '', notes: '', saving: false };

  const setAdj = (id: string, patch: Partial<typeof adjState[string]>) =>
    setAdjState(prev => ({ ...prev, [id]: { ...getAdj(id), ...patch } }));

  const applyMovement = async (g: InventoryGroup): Promise<boolean> => {
    const adj = getAdj(g._id);
    const qty = parseFloat(adj.qty);
    if (isNaN(qty) || qty <= 0) return false;
    setAdj(g._id, { saving: true });
    try {
      const resp = await dispatch(actions.addInventoryMovement, {
        productGroupId: g._id,
        type:   adj.moveType,
        quantity: qty,
        reason: adj.reason,
        notes:  adj.notes,
      }, token);
      if (resp && !resp.error) {
        setAdj(g._id, { qty: '', reason: '', notes: '', saving: false });
        fetchData();
        return true;
      } else {
        setAdj(g._id, { saving: false });
        return false;
      }
    } catch { setAdj(g._id, { saving: false }); return false; }
  };

  const saveGroupName = async (groupId: string) => {
    const newName = editingNameValue.trim();
    setEditingNameId(null);
    if (!newName) return;
    const current = groups.find(g => g._id === groupId);
    if (current && newName === current.name) return;
    await dispatch(actions.editProductGroup, { name: newName }, token, { id: groupId });
    fetchData();
  };

  const applyDirectCount = async (g: InventoryGroup, val: string): Promise<boolean> => {
    const qty = parseFloat(val);
    if (isNaN(qty) || qty < 0) return false;
    const resp = await dispatch(actions.updateInventoryStock, { manualStock: qty }, token, { groupId: g._id });
    if (resp && !resp.error) { fetchData(); return true; }
    return false;
  };

  // ── Jumia sync helpers ─────────────────────────────────────────────────────
  const [syncUi, setSyncUi] = useState<Record<string, SyncUiState>>({});
  const [bulkSyncUi, setBulkSyncUi] = useState<{ running: boolean; done: number; total: number; errors: number } | null>(null);
  const bulkPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const getSync = (id: string): SyncUiState =>
    syncUi[id] ?? { busy: false, bufferDraft: undefined, msg: null, msgKind: null };

  const setSync = (id: string, patch: Partial<SyncUiState>) =>
    setSyncUi(prev => ({ ...prev, [id]: { ...getSync(id), ...patch } }));

  const toggleJumiaSync = async (g: InventoryGroup, enabled: boolean) => {
    setSync(g._id, { busy: true, msg: null, msgKind: null });
    const resp = await dispatch(actions.updateInventorySyncSettings, { jumiaSyncEnabled: enabled }, token, { groupId: g._id });
    if (resp && !resp.error) {
      setSync(g._id, {
        busy: false,
        msg: enabled ? '✓ Auto-sync enabled — pushing stock to Jumia in a few seconds…' : 'Auto-sync disabled',
        msgKind: 'ok',
      });
      fetchData();
    } else {
      setSync(g._id, { busy: false, msg: resp?.error?.message || 'Failed to update sync settings', msgKind: 'err' });
    }
  };

  const toggleJumiaPause = async (g: InventoryGroup, paused: boolean) => {
    const pauseReason = paused ? askPauseReason() : null;
    if (pauseReason === false) return;   // cancelled
    setSync(g._id, { busy: true, msg: null, msgKind: null });
    // ONE pause for Jumia + Marjane (2026-10-13)
    const resp = await dispatch(actions.updateInventorySyncSettings, { jumiaPaused: paused, marjanPaused: paused, pauseReason: pauseReason || undefined }, token, { groupId: g._id });
    if (resp && !resp.error) {
      setSync(g._id, {
        busy: false,
        msg: paused
          ? '⏸ Paused — pushing 0 to every SKU on Jumia and Marjane in a few seconds…'
          : '▶ Resumed — stock is pushed back to every SKU on Jumia and Marjane',
        msgKind: 'ok',
      });
      fetchData();
    } else {
      setSync(g._id, { busy: false, msg: resp?.error?.message || 'Failed to update pause state', msgKind: 'err' });
    }
  };

  const toggleMarketAvailable = async (g: InventoryGroup, on: boolean) => {
    setSync(g._id, { busy: true, msg: null, msgKind: null });
    const resp = await dispatch(actions.updateInventorySyncSettings, { marketAvailable: on }, token, { groupId: g._id });
    if (resp && !resp.error) {
      setSync(g._id, {
        busy: false,
        msg: on
          ? '🛒 Disponible au marché — pushing 1 unit to every SKU on Jumia in a few seconds (0 while an order is open)…'
          : '🛒 Market mode off — SKUs follow the warehouse stock again',
        msgKind: 'ok',
      });
      fetchData();
    } else {
      setSync(g._id, { busy: false, msg: resp?.error?.message || 'Failed to update market mode', msgKind: 'err' });
    }
  };

  const saveSafetyBuffer = async (g: InventoryGroup, val: string) => {
    const buf = parseInt(val, 10);
    if (isNaN(buf) || buf < 0) return;
    setSync(g._id, { busy: true, msg: null, msgKind: null });
    const resp = await dispatch(actions.updateInventorySyncSettings, { safetyBuffer: buf }, token, { groupId: g._id });
    if (resp && !resp.error) {
      setSync(g._id, {
        busy: false, bufferDraft: undefined,
        msg: `✓ Safety buffer set to ${buf}${g.jumiaSyncEnabled ? ' — re-pushing stock…' : ''}`,
        msgKind: 'ok',
      });
      fetchData();
    } else {
      setSync(g._id, { busy: false, msg: resp?.error?.message || 'Failed to save buffer', msgKind: 'err' });
    }
  };

  const syncGroupNow = async (g: InventoryGroup) => {
    setSync(g._id, { busy: true, msg: 'Pushing stock to Jumia — verifying each SKU feed…', msgKind: 'ok' });
    const resp = await dispatch(actions.syncGroupToJumia, {}, token, { groupId: g._id });
    if (resp && !resp.error) {
      const results = (resp.results || []) as { sku: string; ok: boolean; error?: string; skippedDead?: boolean }[];
      const failed = results.filter(r => !r.ok);
      const dead = results.filter(r => r.skippedDead).length;
      const attempted = results.length - dead;
      const deadNote = dead ? ` · ${dead} deleted SKU(s) skipped` : '';
      const okMsg =
        resp.mode === 'paused' ? `✓ Paused — ${attempted} SKU(s) at 0 on Jumia${deadNote}`
        : resp.mode === 'drip' ? `✓ Low stock — 1 unit on ${resp.bestSku || 'best SKU'}, others at 0 (${resp.available} left)${deadNote}`
        : resp.mode === 'out' ? `✓ Out of stock — ${attempted} SKU(s) at 0 on Jumia${deadNote}`
        : `✓ Pushed ${resp.target} to ${attempted} SKU(s) on Jumia${deadNote}`;
      setSync(g._id, {
        busy: false,
        msg: failed.length === 0
          ? okMsg
          : `⚠ ${attempted - failed.length}/${attempted} SKUs updated — failed: ${failed.map(f => `${f.sku} (${f.error})`).join(', ')}${deadNote}`,
        msgKind: failed.length === 0 ? 'ok' : 'err',
      });
      fetchData();
    } else {
      setSync(g._id, { busy: false, msg: resp?.error?.message || 'Sync failed', msgKind: 'err' });
    }
  };

  // ── Marjan sync helpers (same rules, per linked GTIN) ───────────────────────
  const toggleMarjanSync = async (g: InventoryGroup, enabled: boolean) => {
    setSync(g._id, { busy: true, msg: null, msgKind: null });
    const resp = await dispatch(actions.updateInventorySyncSettings, { marjanSyncEnabled: enabled }, token, { groupId: g._id });
    if (resp && !resp.error) {
      setSync(g._id, { busy: false, msg: enabled ? '✓ Marjan auto-sync enabled — pushing stock to Marjan in a few seconds…' : 'Marjan auto-sync disabled', msgKind: 'ok' });
      fetchData();
    } else {
      setSync(g._id, { busy: false, msg: resp?.error?.message || 'Failed to update Marjan sync settings', msgKind: 'err' });
    }
  };

  const toggleMarjanPause = async (g: InventoryGroup, paused: boolean) => {
    const pauseReason = paused ? askPauseReason() : null;
    if (pauseReason === false) return;   // cancelled
    setSync(g._id, { busy: true, msg: null, msgKind: null });
    const resp = await dispatch(actions.updateInventorySyncSettings, { jumiaPaused: paused, marjanPaused: paused, pauseReason: pauseReason || undefined }, token, { groupId: g._id });
    if (resp && !resp.error) {
      setSync(g._id, { busy: false, msg: paused ? '⏸ Paused — pushing 0 to every SKU on Jumia and Marjane in a few seconds…' : '▶ Resumed — stock is pushed back to every SKU on Jumia and Marjane', msgKind: 'ok' });
      fetchData();
    } else {
      setSync(g._id, { busy: false, msg: resp?.error?.message || 'Failed to update Marjan pause state', msgKind: 'err' });
    }
  };

  const syncMarjanGroupNow = async (g: InventoryGroup) => {
    setSync(g._id, { busy: true, msg: 'Pushing stock to Marjan — one call per store…', msgKind: 'ok' });
    const resp = await dispatch(actions.syncGroupToMarjan, {}, token, { groupId: g._id });
    if (resp && !resp.error) {
      const results = (resp.results || []) as { sku: string; ok: boolean; error?: string }[];
      const failed = results.filter(r => !r.ok);
      const okMsg =
        resp.mode === 'paused' ? `✓ Paused — ${results.length} Marjan GTIN(s) at 0`
        : resp.mode === 'drip' ? (resp.target === 1 ? `✓ Low stock — 1 unit on ${resp.bestSku || 'best Marjan SKU'} (${resp.available} left)` : `✓ Low stock — last unit kept for Jumia, Marjan at 0`)
        : resp.mode === 'out' ? `✓ Out of stock — ${results.length} Marjan GTIN(s) at 0`
        : `✓ Pushed ${resp.target} to ${results.length} Marjan GTIN(s)`;
      setSync(g._id, {
        busy: false,
        msg: failed.length === 0 ? okMsg : `⚠ ${results.length - failed.length}/${results.length} GTINs updated — failed: ${failed.map(f => `${f.sku} (${f.error})`).join(', ')}`,
        msgKind: failed.length === 0 ? 'ok' : 'err',
      });
      fetchData();
    } else {
      setSync(g._id, { busy: false, msg: resp?.error?.message || 'Marjan sync failed', msgKind: 'err' });
    }
  };

  const [bulkMarjanUi, setBulkMarjanUi] = useState<{ running: boolean; done: number; total: number; errors: number } | null>(null);
  const bulkMarjanPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollBulkMarjan = useCallback(async () => {
    const st = await dispatch(actions.getMarjanSyncAllStatus, {}, token);
    if (st && !st.error) {
      setBulkMarjanUi({ running: st.running, done: st.done, total: st.total, errors: st.errors });
      if (st.running) bulkMarjanPollRef.current = setTimeout(pollBulkMarjan, 3000);
      else fetchData();
    }
  }, [token, fetchData]);
  const startBulkMarjan = async () => {
    setBulkMarjanUi({ running: true, done: 0, total: 0, errors: 0 });
    const resp = await dispatch(actions.syncAllToMarjan, {}, token);
    if (resp && (resp.queued || resp.progress)) bulkMarjanPollRef.current = setTimeout(pollBulkMarjan, 2000);
    else setBulkMarjanUi(null);
  };
  useEffect(() => () => { if (bulkMarjanPollRef.current) clearTimeout(bulkMarjanPollRef.current); }, []);

  const pollBulkSync = useCallback(async () => {
    const st = await dispatch(actions.getJumiaSyncAllStatus, {}, token);
    if (st && !st.error) {
      setBulkSyncUi({ running: st.running, done: st.done, total: st.total, errors: st.errors });
      if (st.running) {
        bulkPollRef.current = setTimeout(pollBulkSync, 3000);
      } else {
        fetchData();
      }
    }
  }, [token, fetchData]);

  const startBulkSync = async () => {
    setBulkSyncUi({ running: true, done: 0, total: 0, errors: 0 });
    const resp = await dispatch(actions.syncAllToJumia, {}, token);
    if (resp && (resp.queued || resp.progress)) {
      bulkPollRef.current = setTimeout(pollBulkSync, 2000);
    } else {
      setBulkSyncUi(null);
    }
  };

  useEffect(() => () => { if (bulkPollRef.current) clearTimeout(bulkPollRef.current); }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // EXCEL / CSV IMPORT LOGIC
  // ─────────────────────────────────────────────────────────────────────────────
  const parseFile = (file: File) => {
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      const ab = e.target?.result as ArrayBuffer;
      const wb = XLSX.read(ab, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json<string[]>(ws, { header: 1 });
      if (!data || data.length < 2) return;
      const headers = (data[0] as string[]).map(h => String(h ?? '').trim());
      setRawHeaders(headers);
      setRawRows(data.slice(1) as string[][]);
      // auto-map obvious column names
      const autoMap: Record<string, string> = {};
      headers.forEach(h => {
        const l = h.toLowerCase();
        if (l.includes('group') || l.includes('product') || l.includes('nom') || l.includes('name')) {
          if (!Object.values(autoMap).includes('groupName')) autoMap[h] = 'groupName';
        } else if (l.includes('qty') || l.includes('quantit') || l.includes('stock') || l.includes('qté')) {
          if (!Object.values(autoMap).includes('quantity')) autoMap[h] = 'quantity';
        } else if (l.includes('sku') || l.includes('ref') || l.includes('barcode')) {
          if (!Object.values(autoMap).includes('sku')) autoMap[h] = 'sku';
        } else if (l.includes('note') || l.includes('comment') || l.includes('remarque')) {
          if (!Object.values(autoMap).includes('notes')) autoMap[h] = 'notes';
        } else {
          autoMap[h] = '__ignore';
        }
      });
      setColMap(autoMap);
      setImportStep(1);
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault(); setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) parseFile(file);
  };

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) parseFile(file);
  };

  const buildPreview = () => {
    const nameCol   = Object.entries(colMap).find(([, v]) => v === 'groupName')?.[0] ?? '';
    const qtyCol    = Object.entries(colMap).find(([, v]) => v === 'quantity')?.[0] ?? '';
    const skuCol    = Object.entries(colMap).find(([, v]) => v === 'sku')?.[0] ?? '';
    const notesCol  = Object.entries(colMap).find(([, v]) => v === 'notes')?.[0] ?? '';

    const parsed: ParsedRow[] = rawRows
      .filter(row => row.some(c => c !== undefined && c !== ''))
      .map((row, idx) => {
        const cell = (h: string) => h ? String(row[rawHeaders.indexOf(h)] ?? '').trim() : '';
        return {
          rowNumber: idx + 2, // +2: header=1, data starts at 2
          rawData: Object.fromEntries(rawHeaders.map((h, i) => [h, String(row[i] ?? '')])),
          groupName: cell(nameCol),
          sku:       cell(skuCol),
          quantity:  cell(qtyCol),
          notes:     cell(notesCol),
        };
      });
    setPreviewRows(parsed);
    setImportStep(2);
  };

  const runImport = async () => {
    if (!previewRows.length) return;
    setImporting(true);
    try {
      const resp = await dispatch(actions.importInventory, {
        fileName,
        rows: previewRows,
      }, token);
      if (resp && !resp.error) {
        setImportResult(resp);
        setImportStep(3);
        fetchData();
      }
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setImportStep(0); setRawHeaders([]); setRawRows([]);
    setColMap({}); setFileName(''); setPreviewRows([]);
    setImportResult(null); setImporting(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // STOCK MOVEMENT MODAL — computed inline (no sub-component, avoids remount)
  // ─────────────────────────────────────────────────────────────────────────────
  const smGroup   = groups.find(x => x._id === stockModalGroupId) ?? null;
  const smAdj     = smGroup ? getAdj(smGroup._id) : null;
  const smIsAdj   = smAdj ? MOVE_TYPES_ADJ.includes(smAdj.moveType) : false;
  const smCfg     = smAdj ? (MOVEMENT_CFG[smAdj.moveType] ?? MOVEMENT_CFG['OUT']) : null;
  const smBtnBg   = smAdj ? (MOVE_CONFIRM_COLOR[smAdj.moveType] ?? '#6B7280') : '#6B7280';
  const smBtnLabel = !smAdj ? '' : smAdj.saving ? 'Enregistrement…'
    : smIsAdj ? `Set Stock → ${smAdj.qty || '?'}`
    : `${smCfg!.label} — ${smAdj.qty || '?'} unité${parseFloat(smAdj.qty) > 1 ? 's' : ''}`;

  const smHandleConfirm = async () => {
    if (!smGroup || !smAdj) return;
    let ok = false;
    if (smIsAdj) ok = await applyDirectCount(smGroup, smAdj.qty);
    else          ok = await applyMovement(smGroup);
    if (ok) { setStockModalGroupId(null); setShowMoreMoveOptions(false); }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // IMPORT MODAL
  // ─────────────────────────────────────────────────────────────────────────────
  const ImportModal: FC = () => {
    const canNext =
      importStep === 0 ? false :
      importStep === 1 ? (
        Object.values(colMap).includes('groupName') &&
        Object.values(colMap).includes('quantity')
      ) :
      importStep === 2 ? previewRows.length > 0 : false;

    return (
      <div className="ic__modal-overlay" onClick={e => { if (e.target === e.currentTarget) { resetImport(); setShowImport(false); }}}>
        <div className="ic__modal ic__modal--wide">
          <div className="ic__modal-header">
            <Upload size={16} color="#378ADD" />
            <span className="ic__modal-title">Import Inventory from Excel / CSV</span>
            <button className="ic__modal-close" onClick={() => { resetImport(); setShowImport(false); }}>
              <X size={14} />
            </button>
          </div>

          {/* Step indicators */}
          <div className="ic__steps">
            {IMPORT_STEPS.map((label, i) => (
              <Fragment key={i}>
                <div className={`ic__step ${i < importStep ? 'ic__step--done' : i === importStep ? 'ic__step--active' : ''}`}>
                  <div className="ic__step-num">
                    {i < importStep ? <CheckCircle size={12} /> : i + 1}
                  </div>
                  {label}
                </div>
                {i < IMPORT_STEPS.length - 1 && <div className="ic__step-connector" />}
              </Fragment>
            ))}
          </div>

          <div className="ic__modal-body">

            {/* ── Step 0: Upload ── */}
            {importStep === 0 && (
              <div
                className={`ic__upload-zone${dragOver ? ' ic__upload-zone--drag' : ''}`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleFileDrop}
              >
                <div className="ic__upload-zone-icon">📂</div>
                <div className="ic__upload-zone-title">Drop your file here or click to browse</div>
                <div className="ic__upload-zone-sub">Supports Excel (.xlsx, .xls) and CSV files</div>
                <div className="ic__upload-zone-formats">
                  <span className="ic__upload-zone-fmt">.xlsx</span>
                  <span className="ic__upload-zone-fmt">.xls</span>
                  <span className="ic__upload-zone-fmt">.csv</span>
                </div>
                <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }} onChange={handleFileInput} />
              </div>
            )}

            {/* ── Step 1: Map Columns ── */}
            {importStep === 1 && (
              <div>
                <div style={{ fontSize: '0.82rem', color: '#6B7280', marginBottom: '1rem' }}>
                  File: <strong>{fileName}</strong> — {rawHeaders.length} columns, {rawRows.length} rows detected.
                  Map each column to a system field:
                </div>
                <div className="ic__map-grid">
                  {rawHeaders.map(h => (
                    <div key={h} className="ic__map-row">
                      <div className="ic__map-col-name">{h}</div>
                      <span className="ic__map-arrow">→</span>
                      <select className="ic__map-select"
                        value={colMap[h] ?? '__ignore'}
                        onChange={e => setColMap(prev => ({ ...prev, [h]: e.target.value }))}
                      >
                        {SYSTEM_FIELDS.map(f => (
                          <option key={f.key} value={f.key}>{f.label}</option>
                        ))}
                      </select>
                    </div>
                  ))}
                </div>
                {!Object.values(colMap).includes('groupName') && (
                  <div style={{ marginTop: '1rem', padding: '0.5rem 0.75rem', background: '#FEF2F2', borderRadius: 6, fontSize: '0.75rem', color: '#991B1B' }}>
                    ⚠ You must map a column to "Product Group Name" to continue.
                  </div>
                )}
                {!Object.values(colMap).includes('quantity') && (
                  <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', background: '#FEF2F2', borderRadius: 6, fontSize: '0.75rem', color: '#991B1B' }}>
                    ⚠ You must map a column to "Stock Quantity" to continue.
                  </div>
                )}
              </div>
            )}

            {/* ── Step 2: Preview ── */}
            {importStep === 2 && (
              <div>
                <div style={{ fontSize: '0.82rem', color: '#6B7280', marginBottom: '0.875rem' }}>
                  Preview of <strong>{previewRows.length}</strong> rows (showing first 50).
                  Rows with issues will be highlighted.
                </div>
                <div className="ic__preview-table-wrap">
                  <table className="ic__preview-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Product Group</th>
                        <th>Quantity</th>
                        <th>SKU</th>
                        <th>Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewRows.slice(0, 50).map(row => {
                        const qtyNum = parseFloat(row.quantity);
                        const hasIssue = !row.groupName || isNaN(qtyNum) || qtyNum < 0;
                        return (
                          <tr key={row.rowNumber}
                            className={`ic__preview-row--${hasIssue ? 'error' : 'success'}`}>
                            <td style={{ color: '#9CA3AF' }}>{row.rowNumber}</td>
                            <td>
                              {row.groupName
                                ? <strong style={{ color: '#1A1D23' }}>{row.groupName}</strong>
                                : <span style={{ color: '#E24B4A' }}>⚠ Missing</span>
                              }
                            </td>
                            <td>
                              {isNaN(qtyNum)
                                ? <span style={{ color: '#E24B4A' }}>⚠ {row.quantity || 'Missing'}</span>
                                : <strong>{qtyNum}</strong>
                              }
                            </td>
                            <td style={{ color: '#6B7280' }}>{row.sku || '—'}</td>
                            <td style={{ color: '#9CA3AF' }}>{row.notes || '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <div style={{ marginTop: '0.875rem', padding: '0.75rem 1rem', background: '#FFF7ED', borderRadius: 8, fontSize: '0.75rem', color: '#9A3412' }}>
                  <strong>Important:</strong> Importing will <strong>set</strong> (not add) the manual stock for each matched product group.
                  This represents your physical stock count. Movement history will be recorded automatically.
                </div>
              </div>
            )}

            {/* ── Step 3: Results ── */}
            {importStep === 3 && importResult && (
              <div>
                <div className="ic__result-summary">
                  <div className="ic__result-card ic__result-card--success">
                    <div className="ic__result-num">{importResult.successCount}</div>
                    <div className="ic__result-lbl">Successfully Imported</div>
                  </div>
                  <div className="ic__result-card ic__result-card--warning">
                    <div className="ic__result-num">{importResult.warningCount}</div>
                    <div className="ic__result-lbl">Warnings (partial match)</div>
                  </div>
                  <div className="ic__result-card ic__result-card--error">
                    <div className="ic__result-num">{importResult.errorCount}</div>
                    <div className="ic__result-lbl">Failed / Unmatched</div>
                  </div>
                </div>

                {importResult.rows?.filter((r: ImportResultRow) => r.status !== 'success').length > 0 && (
                  <div>
                    <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1A1D23', marginBottom: '0.5rem' }}>
                      Rows requiring attention:
                    </div>
                    <div className="ic__preview-table-wrap">
                      <table className="ic__preview-table">
                        <thead>
                          <tr><th>#</th><th>Group Matched</th><th>Qty</th><th>Status</th><th>Issues</th></tr>
                        </thead>
                        <tbody>
                          {importResult.rows
                            .filter((r: ImportResultRow) => r.status !== 'success')
                            .map((r: ImportResultRow) => (
                              <tr key={r.rowNumber} className={`ic__preview-row--${r.status}`}>
                                <td style={{ color: '#9CA3AF' }}>{r.rowNumber}</td>
                                <td>{r.productGroupName || <span style={{ color: '#9CA3AF' }}>—</span>}</td>
                                <td>{r.quantity ?? '—'}</td>
                                <td>
                                  <span className={`ic__preview-status ic__preview-status--${r.status}`}>
                                    {r.status}
                                  </span>
                                </td>
                                <td className="ic__preview-issues">{r.issues.join(' · ')}</td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="ic__modal-footer">
            {importStep > 0 && importStep < 3 && (
              <button className="ic__topbar-btn" onClick={() => setImportStep(s => s - 1)}>
                ← Back
              </button>
            )}
            <div style={{ flex: 1 }} />
            {importStep === 0 && (
              <button className="ic__topbar-btn" onClick={() => { resetImport(); setShowImport(false); }}>
                Cancel
              </button>
            )}
            {importStep === 1 && (
              <button className="ic__topbar-btn ic__topbar-btn--primary"
                disabled={!canNext}
                onClick={buildPreview}
              >
                Preview →
              </button>
            )}
            {importStep === 2 && (
              <button className="ic__topbar-btn ic__topbar-btn--blue"
                disabled={importing || previewRows.length === 0}
                onClick={runImport}
              >
                {importing ? 'Importing…' : `Import ${previewRows.length} rows →`}
              </button>
            )}
            {importStep === 3 && (
              <button className="ic__topbar-btn ic__topbar-btn--primary"
                onClick={() => { resetImport(); setShowImport(false); }}
              >
                Done
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // ACHAT MODAL
  // ─────────────────────────────────────────────────────────────────────────────
  const AchatModal: FC = () => {
    const g = groups.find(x => x._id === achatGroupId);

    const [qty,    setQty]    = useState('');
    const [price,  setPrice]  = useState('');   // typed every time, never pre-filled with the cost (user 2026-10-24)
    const [saving, setSaving] = useState(false);

    if (!g) return null;

    const inv         = g.manualStock ?? 0;
    const currentRate = g.buyingRate ?? 0;

    const qtyNum   = parseFloat(qty)   || 0;
    const priceNum = parseFloat(price) || 0;
    const priceChanged = Math.abs(priceNum - currentRate) > 0.001;

    const newRate = (qtyNum > 0 && priceNum > 0 && priceChanged)
      ? parseFloat(((inv * currentRate + qtyNum * priceNum) / (inv + qtyNum)).toFixed(2))
      : currentRate;

    const handleConfirm = async () => {
      if (qtyNum <= 0 || priceNum <= 0) return;   // no purchase without its unit price (user 2026-10-23)
      setSaving(true);
      const resp = await dispatch(actions.purchaseInventory, {
        productGroupId: g._id,
        quantity:       qtyNum,
        buyingPrice:    priceNum,
        reason:         'Achat',
      }, token);
      setSaving(false);
      if (resp && !resp.error) {
        setAchatGroupId(null);
        fetchData();
      }
    };

    return (
      <div className="ic__modal-overlay"
        onClick={e => { if (e.target === e.currentTarget) setAchatGroupId(null); }}>
        <div className="ic__sm-modal">

          {/* Header */}
          <div className="ic__sm-header">
            <div className="ic__sm-group-info">
              {g.imgUri
                ? <img src={g.imgUri} alt="" className="ic__sm-group-thumb" />
                : <div className="ic__sm-group-thumb ic__sm-group-thumb--ph">📦</div>
              }
              <div>
                <div className="ic__sm-group-name">{g.name || '(unnamed)'}</div>
                <div className="ic__sm-group-stock">
                  Stock: <strong style={{ color: '#378ADD' }}>{g.manualStock ?? '—'}</strong>
                  {' · '}Buying rate: <strong style={{ color: '#1D9E75' }}>{currentRate.toFixed(2)} MAD</strong>
                </div>
              </div>
            </div>
            <button className="ic__modal-close" onClick={() => setAchatGroupId(null)}>
              <X size={14} />
            </button>
          </div>

          {/* Body */}
          <div className="ic__sm-body">

            <div className="ic__sm-row2">
              <div style={{ flex: 1 }}>
                <div className="ic__sm-section-label">Quantity achetée</div>
                <input
                  type="number" min={1} autoFocus
                  className="ic__sm-qty-input"
                  placeholder="0"
                  value={qty}
                  onChange={e => setQty(e.target.value)}
                />
              </div>
              <div style={{ flex: 1 }}>
                <div className="ic__sm-section-label">Prix d'achat / unité (MAD) — obligatoire *</div>
                <input
                  type="number" min={0} step={0.01}
                  className="ic__sm-qty-input"
                  value={price}
                  placeholder={currentRate ? `coût actuel ${currentRate}` : ''}
                  onChange={e => setPrice(e.target.value)}
                />
              </div>
            </div>

            {/* Weighted average preview */}
            {qtyNum > 0 && priceNum > 0 && (
              <div style={{
                marginTop: '1rem', padding: '0.75rem 1rem',
                background: priceChanged ? '#FFF7ED' : '#F0FDF4',
                border: `1px solid ${priceChanged ? '#FDE68A' : '#A7F3D0'}`,
                borderRadius: 8,
              }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, color: priceChanged ? '#9A3412' : '#065F46', marginBottom: 6 }}>
                  {priceChanged ? '⚠ Prix différent — calcul de la moyenne pondérée' : '✓ Même prix — aucun changement du taux d\'achat'}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                    <span style={{ color: '#6B7280' }}>Stock actuel × ancien prix</span>
                    <span style={{ fontWeight: 600 }}>{inv} × {currentRate.toFixed(2)} = {(inv * currentRate).toFixed(2)} MAD</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem' }}>
                    <span style={{ color: '#6B7280' }}>Nouvel achat × nouveau prix</span>
                    <span style={{ fontWeight: 600 }}>{qtyNum} × {priceNum.toFixed(2)} = {(qtyNum * priceNum).toFixed(2)} MAD</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', borderTop: '1px solid #E5E7EB', paddingTop: 4, marginTop: 2 }}>
                    <span style={{ color: '#6B7280' }}>Nouveau taux d'achat (WAC)</span>
                    <span style={{ fontWeight: 700, color: priceChanged ? '#EA580C' : '#1D9E75' }}>{newRate.toFixed(2)} MAD</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', paddingTop: 2 }}>
                    <span style={{ color: '#6B7280' }}>New Market Price</span>
                    <span style={{ fontWeight: 700, color: '#378ADD' }}>{priceNum.toFixed(2)} MAD</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="ic__sm-footer">
            <button className="ic__topbar-btn" onClick={() => setAchatGroupId(null)}>
              Annuler
            </button>
            <button
              className="ic__sm-confirm-btn"
              style={{ background: '#1D9E75', opacity: (saving || qtyNum <= 0) ? 0.6 : 1 }}
              disabled={saving || qtyNum <= 0 || priceNum <= 0}
              title={priceNum <= 0 ? "Prix d'achat unitaire obligatoire" : undefined}
              onClick={handleConfirm}
            >
              {saving ? 'Enregistrement…' : `Confirmer l'achat (+${qtyNum || 0} unités)`}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Market Update Modal ────────────────────────────────────────────────────
  const MarketModal: FC = () => {
    const g = groups.find(x => x._id === marketGroupId);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
      newMarketPrice:     g?.newMarketPrice ? String(g.newMarketPrice) : '',
      marketAvailability: g?.marketAvailability || '',
      supplierCount:      g?.supplierCount      || '',
      marketVolume:       g?.marketVolume       || '',
      marketNotes:        g?.marketNotes        || '',
    });
    if (!g) return null;

    const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

    const handleSave = async () => {
      setSaving(true);
      const resp = await dispatch(actions.updateMarketInfo, form, token, { groupId: g._id });
      setSaving(false);
      if (resp && !resp.error) { setMarketGroupId(null); fetchData(); }
    };

    return (
      <div className="ic__modal-overlay" onClick={e => { if (e.target === e.currentTarget) setMarketGroupId(null); }}>
        <div className="ic__sm-modal" style={{ maxWidth: 460, width: '95vw' }}>

          {/* Header */}
          <div className="ic__sm-header">
            <div className="ic__sm-group-info">
              {g.imgUri
                ? <img src={g.imgUri} alt="" className="ic__sm-group-thumb" />
                : <div className="ic__sm-group-thumb ic__sm-group-thumb--ph"><Package size={14} /></div>
              }
              <div>
                <div className="ic__sm-group-name">{g.name}</div>
                <div className="ic__sm-group-stock" style={{ color: '#6B7280' }}>Mise à jour marché</div>
              </div>
            </div>
            <button className="ic__modal-close" onClick={() => setMarketGroupId(null)}><X size={14} /></button>
          </div>

          <div className="ic__sm-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

            {/* ── This product's own floor (beats every pricing rule) — saved on its own button ── */}
            <div style={{ background: '#F0FDF4', border: '1px solid #A7F3D0', borderRadius: 10, padding: 10 }}>
              <OwnFloorField groupId={g._id} initial={ownFloors[g._id] ?? null} token={token} />
            </div>

            {/* ── Prix marché ── */}
            <div>
              <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 3 }}>New Market Price (MAD)</div>
              <input className="ic__sm-qty-input" type="number" min={0} step={0.5}
                placeholder={g.newMarketPrice ? String(g.newMarketPrice) : '0'}
                value={form.newMarketPrice} onChange={e => set('newMarketPrice', e.target.value)} />
            </div>

            {/* ── Disponibilité ── */}
            <div>
              <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 6 }}>Disponibilité fsou9</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {([
                  ['moujouda',  'Moujouda'],
                  ['9lila',     '9lila fsou9'],
                  ['mazal',     'Mazal mal9itha'],
                  ['kan9alab',  'Kan9alab aliha'],
                ] as [string, string][]).map(([v, l]) => (
                  <button key={v}
                    onClick={() => set('marketAvailability', form.marketAvailability === v ? '' : v)}
                    style={{
                      padding: '8px 6px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                      border: `2px solid ${form.marketAvailability === v ? '#378ADD' : '#E5E7EB'}`,
                      background: form.marketAvailability === v ? '#EFF6FF' : '#FAFAFA',
                      color: form.marketAvailability === v ? '#378ADD' : '#6B7280',
                    }}>
                    {l}
                  </button>
                ))}
              </div>
            </div>

            {/* ── Fournisseurs & Volume ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div>
                <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 3 }}>Nb fournisseurs</div>
                <select className="ic__sm-qty-input" style={{ cursor: 'pointer' }}
                  value={form.supplierCount} onChange={e => set('supplierCount', e.target.value)}>
                  <option value="">—</option>
                  <option value="1">1</option>
                  <option value="2-3">2 – 3</option>
                  <option value="4+">4+</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 3 }}>Chhal kayn fsou9</div>
                <select className="ic__sm-qty-input" style={{ cursor: 'pointer' }}
                  value={form.marketVolume} onChange={e => set('marketVolume', e.target.value)}>
                  <option value="">—</option>
                  <option value="50-100">50 – 100</option>
                  <option value="100-500">100 – 500</option>
                  <option value="500-1000">500 – 1000</option>
                  <option value="1000+">+1000</option>
                  <option value="no_info">Ma3arfch</option>
                </select>
              </div>
            </div>

            {/* ── Notes ── */}
            <div>
              <div style={{ fontSize: 11, color: '#6B7280', marginBottom: 3 }}>Notes</div>
              <textarea
                className="ic__sm-qty-input"
                style={{ resize: 'vertical', minHeight: 72, fontFamily: 'inherit', fontSize: 13 }}
                placeholder="Observations, remarques..."
                value={form.marketNotes}
                onChange={e => set('marketNotes', e.target.value)}
              />
            </div>

            {g.marketUpdatedAt && (
              <div style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'right' }}>
                Dernière mise à jour : {new Date(g.marketUpdatedAt).toLocaleDateString()}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="ic__sm-footer">
            <button className="ic__topbar-btn" onClick={() => setMarketGroupId(null)}>Annuler</button>
            <button
              className="ic__topbar-btn"
              style={{ background: '#378ADD', color: '#fff', opacity: saving ? 0.6 : 1 }}
              disabled={saving}
              onClick={handleSave}
            >
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── Export to Excel ────────────────────────────────────────────────────────
  const exportToExcel = () => {
    const rows = filtered.map(g => ({
      'Product Group':      g.name,
      'Manual Stock':       g.manualStock ?? '',
      'Damaged Stock':      g.damagedStock,
      'Buying Rate (MAD)':  g.buyingRate,
      'Coverage Days':      g.coverageDays,
      'Velocity 7d':        g.velocity7d,
      'Delivered 30d':      g.delivered30,
      'Returned 30d':       g.returned30,
      'Canceled 30d':       g.canceled30,
      'Pending 30d':        g.pending30,
      'Net Sold 30d':       g.netSold30,
      'Revenue 30d (MAD)':  g.revenue30,
      'Last Stock Count':   fmtDate(g.lastStockCount),
      'Last Updated':       fmtDate(g.lastUpdated),
      'Location':           g.location,
      'Notes':              g.notes,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Inventory');
    const date = new Date().toISOString().slice(0, 10);
    XLSX.writeFile(wb, `inventory_${date}.xlsx`);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div className="ic">
      {contextStore?.user?.type === 'admin' && <PauseReminders onChange={() => fetchData()} />}
      {showImport && <ImportModal />}
      {stockModalGroupId && smGroup && smAdj && (
        <div className="ic__modal-overlay"
          onClick={e => { if (e.target === e.currentTarget) { setStockModalGroupId(null); setShowMoreMoveOptions(false); } }}>
          <div className="ic__sm-modal">

            <div className="ic__sm-header">
              <div className="ic__sm-group-info">
                {smGroup.imgUri
                  ? <img src={smGroup.imgUri} alt="" className="ic__sm-group-thumb" />
                  : <div className="ic__sm-group-thumb ic__sm-group-thumb--ph">📦</div>
                }
                <div>
                  <div className="ic__sm-group-name">{smGroup.name || '(unnamed)'}</div>
                  <div className="ic__sm-group-stock">
                    Stock: <strong style={{ color: '#378ADD' }}>{smGroup.manualStock ?? '—'}</strong> unités
                  </div>
                </div>
              </div>
              <button className="ic__modal-close" onClick={() => { setStockModalGroupId(null); setShowMoreMoveOptions(false); }}>
                <X size={14} />
              </button>
            </div>

            <div className="ic__sm-body">
              <div className="ic__sm-primary-grid">
                {MOVE_BTNS_PRIMARY.map(({ t, label, Icon, color, bg, border }) => {
                  const active = smAdj.moveType === t;
                  return (
                    <button key={t}
                      className={`ic__sm-primary-btn${active ? ' ic__sm-primary-btn--active' : ''}`}
                      style={active ? { background: bg, borderColor: border, color } : {}}
                      onClick={() => setAdj(smGroup._id, { moveType: t })}
                    >
                      <Icon size={22} color={active ? color : '#9CA3AF'} strokeWidth={2.2} />
                      <span>{label}</span>
                    </button>
                  );
                })}
              </div>

              <button onClick={() => setShowMoreMoveOptions(s => !s)} style={{
                display: 'flex', alignItems: 'center', gap: 6,
                fontSize: '0.72rem', fontWeight: 600, color: '#9CA3AF',
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '0.25rem 0', letterSpacing: '0.03em', fontFamily: 'inherit',
              }}>
                <span style={{ fontSize: '0.65rem' }}>{showMoreMoveOptions ? '▲' : '▼'}</span>
                {showMoreMoveOptions ? "Moins d'options" : "Plus d'options"}
              </button>

              {showMoreMoveOptions && (
                <div className="ic__sm-type-grid">
                  {MOVE_BTNS_MORE.map(({ t, label, Icon, color, bg, border }) => {
                    const active = smAdj.moveType === t;
                    return (
                      <button key={t} className="ic__sm-type-btn"
                        style={active ? { background: bg, borderColor: border, color } : {}}
                        onClick={() => setAdj(smGroup._id, { moveType: t })}
                      >
                        <Icon size={14} color={active ? color : '#9CA3AF'} strokeWidth={2.5} />
                        <span>{label}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              <div className="ic__sm-section-label" style={{ marginTop: '0.75rem' }}>
                {smIsAdj ? 'Nouveau stock total' : 'Quantité'}
              </div>
              <input type="number" min={0} autoFocus
                className="ic__sm-qty-input"
                placeholder={smIsAdj ? `Actuel: ${smGroup.manualStock ?? '?'}` : '0'}
                value={smAdj.qty}
                onChange={e => setAdj(smGroup._id, { qty: e.target.value })}
              />

              <div style={{ marginTop: '0.75rem' }}>
                <div className="ic__sm-section-label">Notes (optionnel)</div>
                <input type="text" placeholder="Détails supplémentaires…"
                  className="ic__sm-field"
                  value={smAdj.notes}
                  onChange={e => setAdj(smGroup._id, { notes: e.target.value })}
                />
              </div>
            </div>

            <div className="ic__sm-footer">
              <button className="ic__topbar-btn" onClick={() => { setStockModalGroupId(null); setShowMoreMoveOptions(false); }}>
                Annuler
              </button>
              <button className="ic__sm-confirm-btn"
                style={{ background: smBtnBg, opacity: (smAdj.saving || !smAdj.qty) ? 0.6 : 1 }}
                disabled={smAdj.saving || !smAdj.qty}
                onClick={smHandleConfirm}
              >
                {smBtnLabel}
              </button>
            </div>
          </div>
        </div>
      )}
      {achatGroupId      && <AchatModal />}
      {marketGroupId     && <MarketModal />}
      {imgPopup && (
        <div className="ic__img-popup" style={{ left: imgPopup.x, top: imgPopup.y }}>
          <img src={imgPopup.src} alt="Product" />
        </div>
      )}

      {showCreateGroup && (
        <CreateGroupModal
          token={token}
          onCreated={() => { setShowCreateGroup(false); fetchData(); }}
          onClose={() => setShowCreateGroup(false)}
        />
      )}

      {showMergeGroups && (
        <MergeGroupsModal
          groups={groups
            .filter(g => selectedGroupIds.has(g._id))
            .map(g => ({
              _id: g._id,
              name: g.name,
              imageUrl: g.imgUri,
              skuCount: g.totalSkus,
              buyingRate: g.buyingRate,
              transportFees: g.transportFees,
              storageFees: g.storageFees,
              packageFees: g.packageFees,
              category: undefined,
              supplier: undefined,
            } as MergeableGroup))}
          token={token}
          onMerged={() => { setShowMergeGroups(false); setSelectedGroupIds(new Set()); fetchData(); }}
          onClose={() => setShowMergeGroups(false)}
        />
      )}

      {/* ── Top bar ────────────────────────────────────────────────────────── */}
      <div className="ic__topbar">
        <div className="ic__title-block">
          <div className="ic__title-icon">
            <ClipboardList size={15} color="#fff" strokeWidth={2} />
          </div>
          <div>
            <div className="ic__title">Inventory Control Center</div>
            <div className="ic__subtitle">Manual stock · Excel import · movement history</div>
          </div>
        </div>

        {stores.length > 0 && (
          <select className="ic__select" value={storeId} onChange={e => setStoreId(e.target.value)}>
            <option value="0">All Stores</option>
            {stores.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
          </select>
        )}

        {lastUpdated && (
          <span className="ic__last-updated">Updated {lastUpdated.toLocaleTimeString()}</span>
        )}

        {!isPhone && renderSecondaryActions()}

        <button className="ic__topbar-btn ic__topbar-btn--primary"
          onClick={() => setShowCreateGroup(true)}
        >
          <Plus size={13} /> New Group
        </button>

        <button className="ic__topbar-btn ic__topbar-btn--primary"
          onClick={fetchData} disabled={loading}
        >
          <RefreshCw size={13} className={loading ? 'ic__spin' : ''} />
          {loading ? 'Loading…' : 'Refresh'}
        </button>
        {isPhone && (
          <div className="ic__more">
            <button className="ic__topbar-btn" onClick={() => setMoreOpen((v) => !v)} aria-label="More actions">⋯</button>
            {moreOpen && <div className="ic__more-menu" onClick={() => setMoreOpen(false)}>{renderSecondaryActions()}</div>}
          </div>
        )}
      </div>

      <div className="ic__body">
        {/* loading / error (2026-10-13): never a silent empty page — on a phone it looked broken */}
        {loading && !groups.length && <div className="ic__loadnote">⏳ Loading the products…</div>}
        {loadError && !loading && (
          <div className="ic__loaderr">⚠️ The products could not be loaded: {loadError} <button onClick={fetchData}>Retry</button></div>
        )}


        {/* ── KPI Strip ──────────────────────────────────────────────────────── */}
        <div className="ic__kpi-grid">
          {[
            {
              icon: <Layers size={13} color="#378ADD" />, iconBg: '#EFF6FF',
              label: 'Total Groups', colorCls: 'ic__kpi-value--blue',
              value: loading ? '—' : String(kpis.total),
              sub: `${kpis.withData} with manual stock`,
            },
            {
              icon: <Package size={13} color="#1D9E75" />, iconBg: '#F0FDF4',
              label: 'Manual Stock (units)', colorCls: 'ic__kpi-value--green',
              value: loading ? '—' : kpis.totalManual.toLocaleString(),
              sub: `${kpis.withData} groups counted`,
            },
            {
              icon: <AlertTriangle size={13} color="#EF9F27" />, iconBg: '#FFFBEB',
              label: 'Damaged Stock (units)', colorCls: kpis.damaged > 0 ? 'ic__kpi-value--amber' : 'ic__kpi-value--green',
              value: loading ? '—' : kpis.damaged.toLocaleString(),
              sub: 'across all groups',
            },
            {
              icon: <FileText size={13} color="#9CA3AF" />, iconBg: '#F9FAFB',
              label: 'No Inventory Data', colorCls: kpis.noData > 0 ? 'ic__kpi-value--amber' : 'ic__kpi-value--green',
              value: loading ? '—' : String(kpis.noData),
              sub: 'groups not yet counted',
            },
          ].map((k, i) => (
            <div key={i} className="ic__kpi">
              <div className="ic__kpi-header">
                <div className="ic__kpi-icon" style={{ background: k.iconBg }}>{k.icon}</div>
                <span className="ic__kpi-label">{k.label}</span>
              </div>
              <div className={`ic__kpi-value ${k.colorCls}`}>{k.value}</div>
              <div className="ic__kpi-sub">{k.sub}</div>
            </div>
          ))}
        </div>

        {/* ── Filter bar ─────────────────────────────────────────────────────── */}
        <div className="ic__filter-bar">
          <div className="ic__search">
            <Search size={13} color="#9CA3AF" />
            <input
              placeholder="Search product group…"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <div className="ic__filter-divider" />
          <span className="ic__filter-label">Manual Stock</span>
          <select className="ic__filter-select" value={filterStock} onChange={e => setFilterStock(e.target.value)}>
            <option value="all">All ({groups.length})</option>
            <option value="__floor">🛡 Has its own floor ({groups.filter(g => ownFloors[g._id] != null).length})</option>
            {stockOptions.map(o => (
              <option key={o.key} value={o.key}>
                {o.label === 'No Data' ? `No Data (${o.count})` : `${o.label} units (${o.count})`}
              </option>
            ))}
          </select>
          <span className="ic__count-badge">{filtered.length} groups</span>
        </div>

        {/* ── Merge action bar ────────────────────────────────────────────────── */}
        {selectedGroupIds.size >= 2 && (
          <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 16px', background:'#EEF4FF', border:'1px solid #378ADD', borderRadius:10, marginBottom:12 }}>
            <span style={{ fontSize:'0.82rem', fontWeight:600, color:'#378ADD' }}>{selectedGroupIds.size} groups selected</span>
            <div style={{ flex:1 }} />
            <button className="ic__topbar-btn" onClick={() => setSelectedGroupIds(new Set())}>Deselect all</button>
            <button className="ic__topbar-btn ic__topbar-btn--blue" onClick={() => setShowMergeGroups(true)}>
              Merge {selectedGroupIds.size} Groups
            </button>
          </div>
        )}

        {/* ── Phone: one card per product (the table is 1,100 px wide) ─────────── */}
        {isPhone && (
          <div className="ic__cards">
            {loading && !groups.length ? null : filtered.length === 0 ? (
              <div className="ic__empty"><div className="ic__empty-msg">{groups.length === 0 ? 'No product groups found.' : 'No groups match your current filter.'}</div></div>
            ) : filtered.slice(0, phoneLimit).map(g => {
              const isExpanded = expandedId === g._id;
              const open = g.pending30 + g.rts30 + (g.marjanOpenOrders || 0);
              const avail = Math.max(0, (g.manualStock ?? 0) - open);
              const cc = covColor(g.coverageDays, g.velocity7d > 0);
              return (
                <div key={g._id} className={`ic__card${isExpanded ? ' ic__card--open' : ''}`}>
                  <div className="ic__card-head" onClick={() => setExpandedId(isExpanded ? null : g._id)}>
                    {g.imgUri ? <img src={g.imgUri} alt="" className="ic__card-img" /> : <div className="ic__card-img ic__group-img-placeholder">📦</div>}
                    <div className="ic__card-title">
                      <div className="ic__card-name">{g.name || '(unnamed)'}</div>
                      <div className="ic__group-meta">
                        <span className="ic__sku-pill">{g.totalSkus} SKUs</span>
                        {ownFloors[g._id] != null && <span className="ic__sku-pill" title="This product's own floor: minimum profit per sale, everywhere (Jumia, Marjane, Auto-Pilot). Change it in Info." style={{ background: '#ECFDF5', color: '#047857' }}>🛡 {ownFloors[g._id]} MAD</span>}
                        {g.jumiaPaused && <span className="ic__sku-pill" style={{ background: '#FEF2F2', color: '#E24B4A' }}>⏸ Paused</span>}
                        {g.marketAvailable && !g.jumiaPaused && <span className="ic__sku-pill" style={{ background: '#F5F3FF', color: '#7C3AED' }}>🛒 Marché</span>}
                        {(g.lastJumiaSyncStatus === 'failed' || g.lastMarjanSyncStatus === 'failed') && <span className="ic__sku-pill" style={{ background: '#FEF2F2', color: '#E24B4A' }}>⚡ sync failed</span>}
                        {g.location && <span className="ic__sku-pill">📍 {g.location}</span>}
                      </div>
                    </div>
                    {isExpanded ? <ChevronDown size={18} color="#6B7280" /> : <ChevronRight size={18} color="#6B7280" />}
                  </div>
                  <div className="ic__card-stats" onClick={() => setExpandedId(isExpanded ? null : g._id)}>
                    <div><b className={g.manualStock == null ? 'ic__stock-num--gray' : (g.manualStock as number) < 0 ? 'ic__stock-num--red' : 'ic__stock-num--blue'}>{g.manualStock ?? '—'}</b><span>Stock</span></div>
                    <div><b>{g.manualStock == null ? '—' : avail}</b><span>Available</span></div>
                    <div><b style={{ color: open ? '#EF9F27' : undefined }}>{open}</b><span>Waiting</span></div>
                    <div><b>{g.buyingRate ? `${Math.round(g.buyingRate)}` : '—'}</b><span>Buying MAD</span></div>
                    <div><b>{g.delivered30 + g.pending30 + g.shipped30 + g.rts30}</b><span>Orders 30d</span></div>
                    <div><b className={cc.text}>{g.velocity7d === 0 ? '—' : g.coverageDays >= 9999 ? '∞' : `${fmt1(g.coverageDays)}d`}</b><span>Lasts</span></div>
                    {g.damagedStock > 0 && <div><b className="ic__stock-num--amber">{g.damagedStock}</b><span>Damaged</span></div>}
                  </div>
                  <div className="ic__card-actions">
                    <button className="ic__quick-move-btn" onClick={() => setStockModalGroupId(g._id)}><Zap size={12} /> Move stock</button>
                    <button className="ic__quick-move-btn" style={{ background: '#F0FDF4', color: '#1D9E75', borderColor: '#A7F3D0' }} onClick={() => setAchatGroupId(g._id)}><Plus size={12} /> Achat</button>
                    <button className="ic__quick-move-btn" disabled={getSync(g._id).busy} style={{ background: g.jumiaPaused ? '#FEF2F2' : undefined, color: g.jumiaPaused ? '#E24B4A' : undefined }} onClick={() => toggleJumiaPause(g, !g.jumiaPaused)}>{g.jumiaPaused ? '▶ Resume' : '⏸ Pause'}</button>
                    <button className="ic__quick-move-btn" disabled={getSync(g._id).busy || g.jumiaPaused} onClick={() => toggleMarketAvailable(g, !g.marketAvailable)}>🛒 {g.marketAvailable ? 'Marché ✓' : 'Marché'}</button>
                    <button className="ic__quick-move-btn" onClick={() => setMarketGroupId(g._id)}><BarChart2 size={12} /> Info</button>
                  </div>
                  {isExpanded && (
                    <div className="ic__card-detail">
                      <div className="ic__card-line">Last count: <b>{g.lastStockCount ? fmtDate(g.lastStockCount) : 'never'}</b> · returned (30 d): <b>{g.returned30}</b> · {fmt1(g.velocity7d)}/day</div>
                      <DetailPanel
                        g={g}
                        adj={getAdj(g._id)}
                        onSetAdj={patch => setAdj(g._id, patch)}
                        onApplyMovement={() => applyMovement(g)}
                        onApplyDirectCount={qty => applyDirectCount(g, qty)}
                        sync={getSync(g._id)}
                        onToggleSync={enabled => toggleJumiaSync(g, enabled)}
                        onTogglePause={paused => toggleJumiaPause(g, paused)}
                        onToggleMarket={on => toggleMarketAvailable(g, on)}
                        onSetBufferDraft={v => setSync(g._id, { bufferDraft: v })}
                        onSaveBuffer={v => saveSafetyBuffer(g, v)}
                        onSyncNow={() => syncGroupNow(g)}
                        onToggleMarjanSync={enabled => toggleMarjanSync(g, enabled)}
                        onToggleMarjanPause={paused => toggleMarjanPause(g, paused)}
                        onSyncMarjanNow={() => syncMarjanGroupNow(g)}
                      />
                      <PackFloorSection groupId={g._id} allGroups={groups} />
                    </div>
                  )}
                </div>
              );
            })}
            {filtered.length > phoneLimit && <button className="ic__card-more" onClick={() => setPhoneLimit((n) => n + 40)}>Show more ({filtered.length - phoneLimit} left)</button>}
          </div>
        )}

        {/* ── Main Inventory Table ────────────────────────────────────────────── */}
        {!isPhone && <div className="ic__table-wrap">
          <div className="ic__table-scroll">
          <table className="ic__table">
            <thead>
              <tr>
                <th className="ic__th" style={{ width: 32 }} />
                <th style={{ width: 32 }} />
                <th>Product Group</th>
                {[
                  { key: 'manual',      label: 'Manual Stock' },
                  { key: 'damaged',     label: 'Damaged'      },
                ].map(({ key, label }) => (
                  <th key={key} className="ic__th--sort" onClick={() => toggleSort(key)}>
                    <span>{label}</span>
                    <span className={`ic__sort-icon${sortCol === key ? ' ic__sort-icon--active' : ''}`}>
                      {sortCol === key ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                    </span>
                  </th>
                ))}
                {[
                  { key: 'coverage', label: 'Coverage'   },
                  { key: 'orders',   label: 'Orders 30d' },
                  { key: 'marketinfo', label: 'Infos marché' },
                  { key: 'lastcount',label: 'Last Count' },
                ].map(({ key, label }) => (
                  <th key={key} className="ic__th--sort" onClick={() => toggleSort(key)}>
                    <span>{label}</span>
                    <span className={`ic__sort-icon${sortCol === key ? ' ic__sort-icon--active' : ''}`}>
                      {sortCol === key ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                    </span>
                  </th>
                ))}
                <th style={{ width: 170, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="ic__skel-row">
                    {[32, 32, 200, 80, 70, 80, 70, 110, 80].map((w, j) => (
                      <td key={j} style={{ padding: '0.75rem 0.875rem' }}>
                        <div className="ic__skel" style={{ height: 12, width: w }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={10}>
                    <div className="ic__empty">
                      <div className="ic__empty-icon">📋</div>
                      <div className="ic__empty-msg">
                        {groups.length === 0
                          ? 'No product groups found. Add product groups first.'
                          : 'No groups match your current filter.'}
                      </div>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map(g => {
                  const isExpanded = expandedId === g._id;
                  const cc         = covColor(g.coverageDays, g.velocity7d > 0);

                  return (
                    <Fragment key={g._id}>
                      <tr
                        className={`ic__row${isExpanded ? ' ic__row--expanded' : ''}`}
                        onClick={() => setExpandedId(isExpanded ? null : g._id)}
                      >
                        {/* checkbox */}
                        <td className="ic__cell" style={{ width: 32, textAlign: 'center' }}
                          onClick={e => e.stopPropagation()}>
                          <input type="checkbox"
                            checked={selectedGroupIds.has(g._id)}
                            onChange={() => setSelectedGroupIds(prev => {
                              const n = new Set(prev);
                              n.has(g._id) ? n.delete(g._id) : n.add(g._id);
                              return n;
                            })}
                            style={{ cursor: 'pointer', accentColor: '#378ADD' }}
                          />
                        </td>

                        {/* expand */}
                        <td className="ic__cell" style={{ width: 32, textAlign: 'center' }}>
                          <div className="ic__expand-btn">
                            {isExpanded
                              ? <ChevronDown size={12} color="#6B7280" />
                              : <ChevronRight size={12} color="#6B7280" />}
                          </div>
                        </td>

                        {/* group name + image */}
                        <td className="ic__cell">
                          <div className="ic__group-cell">
                            <div className="ic__group-img-wrap"
                              onMouseEnter={e => {
                                if (g.imgUri) {
                                  const r = e.currentTarget.getBoundingClientRect();
                                  setImgPopup({ src: g.imgUri, x: r.left, y: r.bottom + 8 });
                                }
                              }}
                              onMouseLeave={() => setImgPopup(null)}
                            >
                              {g.imgUri
                                ? <img src={g.imgUri} alt="" className="ic__group-img" />
                                : <div className="ic__group-img-placeholder">📦</div>
                              }
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              {editingNameId === g._id ? (
                                <input
                                  autoFocus
                                  className="ic__name-edit-input"
                                  value={editingNameValue}
                                  onChange={e => setEditingNameValue(e.target.value)}
                                  onBlur={() => saveGroupName(g._id)}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') saveGroupName(g._id);
                                    if (e.key === 'Escape') setEditingNameId(null);
                                  }}
                                  onClick={e => e.stopPropagation()}
                                />
                              ) : (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <span className="ic__group-name">{g.name || '(unnamed)'}</span>
                                  <button
                                    className="ic__name-edit-btn"
                                    title="Renommer"
                                    onClick={e => {
                                      e.stopPropagation();
                                      setEditingNameValue(g.name || '');
                                      setEditingNameId(g._id);
                                    }}
                                  >✎</button>
                                </div>
                              )}
                              <div className="ic__group-meta">
                                <span className="ic__sku-pill">{g.totalSkus} SKU{g.totalSkus !== 1 ? 's' : ''}</span>
                                {ownFloors[g._id] != null && <span className="ic__sku-pill" title="This product's own floor: minimum profit per sale, everywhere (Jumia, Marjane, Auto-Pilot). Change it in Info." style={{ background: '#ECFDF5', color: '#047857' }}>🛡 {ownFloors[g._id]} MAD</span>}
                                {g.jumiaPaused && (
                                  <span className="ic__sku-pill"
                                    title="Paused — every SKU pushed to 0 on Jumia and Marjane"
                                    style={{ background: '#FEF2F2', color: '#E24B4A' }}>
                                    ⏸ Paused everywhere
                                  </span>
                                )}

                                {g.marketAvailable && !g.jumiaPaused && (
                                  <span className="ic__sku-pill"
                                    title="Disponible au marché — no stock here: every SKU shows 1 on Jumia, 0 while an order is open"
                                    style={{ background: '#F5F3FF', color: '#7C3AED' }}>
                                    🛒 Marché
                                  </span>
                                )}
                                {(() => {
                                  // one sync badge for both platforms: red when the last push failed on either
                                  const bad = g.lastJumiaSyncStatus === 'failed' || g.lastMarjanSyncStatus === 'failed';
                                  return (
                                    <span className="ic__sku-pill"
                                      title={`Stock sync (Jumia + Marjane) · buffer ${g.safetyBuffer} · last: Jumia ${g.lastJumiaSyncStatus || 'never'}${g.totalMarjanSkus ? ` / Marjane ${g.lastMarjanSyncStatus || 'never'}` : ''}`}
                                      style={{ background: bad ? '#FEF2F2' : '#F0FDF4', color: bad ? '#E24B4A' : '#1D9E75' }}>
                                      ⚡ Sync
                                    </span>
                                  );
                                })()}
                                {g.location && (
                                  <span className="ic__sku-pill">📍 {g.location}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* manual stock */}
                        <td className="ic__cell">
                          <div className="ic__stock-cell">
                            <span className={`ic__stock-num ${g.manualStock === null ? 'ic__stock-num--gray' : (g.manualStock as number) < 0 ? 'ic__stock-num--red' : 'ic__stock-num--blue'}`}>
                              {g.manualStock !== null ? g.manualStock : '—'}
                            </span>
                            {!g.hasInventoryRecord && (
                              <span className="ic__stock-sub">Not counted</span>
                            )}
                          </div>
                        </td>

                        {/* damaged stock */}
                        <td className="ic__cell">
                          <div className="ic__stock-cell">
                            <span className={`ic__stock-num ${g.damagedStock > 0 ? 'ic__stock-num--amber' : 'ic__stock-num--gray'}`}>
                              {g.damagedStock > 0 ? g.damagedStock : '—'}
                            </span>
                            {g.damagedStock > 0 && (
                              <span className="ic__stock-sub">⚠ damaged</span>
                            )}
                          </div>
                        </td>

                        {/* coverage */}
                        <td className="ic__cell">
                          <div className="ic__cov">
                            <div className="ic__cov-row">
                              <span className={`ic__cov-val ${cc.text}`}>
                                {g.velocity7d === 0 ? '—'
                                  : g.coverageDays >= 9999 ? '∞'
                                  : `${fmt1(g.coverageDays)}d`}
                              </span>
                              {g.velocity7d > 0 && g.coverageDays < 9999 && (
                                <span className="ic__cov-unit">left</span>
                              )}
                            </div>
                            {g.velocity7d > 0 && (
                              <div className="ic__cov-track">
                                <div className="ic__cov-fill"
                                  style={{ width: `${clamp((g.coverageDays / 60) * 100, 2, 100)}%`, background: cc.bar }} />
                              </div>
                            )}
                            <div className="ic__stock-sub">{fmt1(g.velocity7d)}/day</div>
                          </div>
                        </td>

                        {/* orders 30d */}
                        <td className="ic__cell">
                          <div className="ic__stock-cell">
                            <span className="ic__stock-num">{g.delivered30 + g.pending30 + g.shipped30 + g.rts30}</span>
                            <span className="ic__stock-sub">{g.returned30} returned</span>
                          </div>
                        </td>

                        {/* market info */}
                        <td className="ic__cell" style={{ minWidth: 120 }}>
                          {(g.marketAvailability || g.supplierCount || g.marketVolume) ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {g.marketAvailability && (
                                <span style={{
                                  fontSize: '0.68rem', fontWeight: 600, padding: '1px 5px', borderRadius: 4, width: 'fit-content',
                                  background: g.marketAvailability === 'moujouda' ? '#F0FDF4' : g.marketAvailability === '9lila' ? '#FFFBEB' : g.marketAvailability === 'mazal' ? '#EFF6FF' : '#FEF2F2',
                                  color:      g.marketAvailability === 'moujouda' ? '#1D9E75' : g.marketAvailability === '9lila' ? '#F59E0B' : g.marketAvailability === 'mazal' ? '#378ADD' : '#EF4444',
                                }}>
                                  {g.marketAvailability === 'moujouda' ? 'Moujouda' : g.marketAvailability === '9lila' ? '9lila fsou9' : g.marketAvailability === 'mazal' ? 'Mazal mal9itha' : 'Kan9alab aliha'}
                                </span>
                              )}
                              <span style={{ fontSize: '0.68rem', color: '#6B7280' }}>
                                {[
                                  g.supplierCount && `${g.supplierCount} fournisseur${g.supplierCount === '1' ? '' : 's'}`,
                                  g.marketVolume  && (g.marketVolume === 'no_info' ? 'Ma3arfch' : `${g.marketVolume} u.`),
                                ].filter(Boolean).join(' · ')}
                              </span>
                            </div>
                          ) : (
                            <span style={{ fontSize: '0.68rem', color: '#D1D5DB' }}>—</span>
                          )}
                        </td>

                        {/* last count */}
                        <td className="ic__cell">
                          <span style={{ fontSize: '0.72rem', color: g.lastStockCount ? '#6B7280' : '#9CA3AF' }}>
                            {g.lastStockCount ? fmtDate(g.lastStockCount) : 'Never counted'}
                          </span>
                        </td>

                        {/* quick actions */}
                        <td className="ic__cell" style={{ textAlign: 'center' }}
                          onClick={e => e.stopPropagation()}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
                            <button
                              className="ic__quick-move-btn"
                              onClick={() => setStockModalGroupId(g._id)}
                            >
                              <Zap size={11} strokeWidth={2.5} />
                              Move Stock
                            </button>
                            <button
                              className="ic__quick-move-btn"
                              style={{
                                background:  g.jumiaPaused ? '#FEF2F2' : '#F9FAFB',
                                color:       g.jumiaPaused ? '#E24B4A' : '#6B7280',
                                borderColor: g.jumiaPaused ? '#FECACA' : '#D1D5DB',
                              }}
                              disabled={getSync(g._id).busy}
                              title={g.jumiaPaused
                                ? 'Paused — every SKU at 0 on Jumia and Marjane. Click to resume everywhere.'
                                : 'Pause: keep the inventory but push 0 to every SKU on Jumia and Marjane'}
                              onClick={() => toggleJumiaPause(g, !g.jumiaPaused)}
                            >
                              {g.jumiaPaused ? '▶ Resume' : '⏸ Pause'}
                            </button>
                            {/* one pause for Jumia + Marjane (2026-10-13): no separate Marjane button */}
                            <button
                              className="ic__quick-move-btn"
                              style={{
                                background:  g.marketAvailable ? '#F5F3FF' : '#F9FAFB',
                                color:       g.marketAvailable ? '#7C3AED' : '#6B7280',
                                borderColor: g.marketAvailable ? '#DDD6FE' : '#D1D5DB',
                              }}
                              disabled={getSync(g._id).busy || g.jumiaPaused}
                              title={g.marketAvailable
                                ? 'Market mode ON — every SKU shows 1 on Jumia while no order is open. Click to turn off.'
                                : 'Disponible au marché: no stock here but buyable at the market — push 1 to every SKU to test demand'}
                              onClick={() => toggleMarketAvailable(g, !g.marketAvailable)}
                            >
                              🛒 {g.marketAvailable ? 'Marché ✓' : 'Marché'}
                            </button>
                            <button
                              className="ic__quick-move-btn"
                              style={{ background: '#F0FDF4', color: '#1D9E75', borderColor: '#A7F3D0' }}
                              onClick={() => setAchatGroupId(g._id)}
                            >
                              <Plus size={11} strokeWidth={2.5} />
                              Achat
                            </button>
                            <button
                              className="ic__quick-move-btn"
                              style={{
                                background: g.reorderSignal === 'buy_more' ? '#F0FDF4' : g.reorderSignal === 'stop' ? '#FEF2F2' : g.reorderSignal === 'reduce' ? '#FFFBEB' : '#EFF6FF',
                                color:      g.reorderSignal === 'buy_more' ? '#1D9E75' : g.reorderSignal === 'stop' ? '#EF4444' : g.reorderSignal === 'reduce' ? '#F59E0B' : '#378ADD',
                                borderColor:g.reorderSignal === 'buy_more' ? '#A7F3D0' : g.reorderSignal === 'stop' ? '#FECACA' : g.reorderSignal === 'reduce' ? '#FDE68A' : '#BFDBFE',
                              }}
                              onClick={() => setMarketGroupId(g._id)}
                            >
                              <BarChart2 size={11} strokeWidth={2.5} />
                              Info Updates
                            </button>
                          </div>
                        </td>
                      </tr>

                      {/* expanded detail */}
                      {isExpanded && (
                        <tr className="ic__expanded-row">
                          <td colSpan={10}>
                            <DetailPanel
                              g={g}
                              adj={getAdj(g._id)}
                              onSetAdj={patch => setAdj(g._id, patch)}
                              onApplyMovement={() => applyMovement(g)}
                              onApplyDirectCount={qty => applyDirectCount(g, qty)}
                              sync={getSync(g._id)}
                              onToggleSync={enabled => toggleJumiaSync(g, enabled)}
                              onTogglePause={paused => toggleJumiaPause(g, paused)}
                              onToggleMarket={on => toggleMarketAvailable(g, on)}
                              onSetBufferDraft={v => setSync(g._id, { bufferDraft: v })}
                              onSaveBuffer={v => saveSafetyBuffer(g, v)}
                              onSyncNow={() => syncGroupNow(g)}
                              onToggleMarjanSync={enabled => toggleMarjanSync(g, enabled)}
                              onToggleMarjanPause={paused => toggleMarjanPause(g, paused)}
                              onSyncMarjanNow={() => syncMarjanGroupNow(g)}
                            />
                            <PackFloorSection groupId={g._id} allGroups={groups} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })
              )}
            </tbody>
          </table>
          </div>
        </div>}
      </div>
    </div>
  );
};

export default InventoryControlPage;
